import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type CSSProperties,
  type ReactNode
} from 'react';
import { api, ApiClientError } from './api';
import { I18nProvider, permissionLabel, useI18n } from './i18n';
import type {
  AdminNotificationItem,
  AdminPendingAuthItem,
  AuditLogItem,
  CommandQueueItem,
  CreatedInviteCode,
  DashboardData,
  ErrorLogItem,
  InviteCodeCheckBatchItem,
  InviteCodeCheckBatchResponse,
  InviteCodeItem,
  Locale,
  NotificationAttachmentItem,
  NotificationItem,
  PendingAuthRequest,
  PlayerItem,
  ServerItem,
  SkinStatePayload,
  LoginPasswordStatePayload,
  SessionPayload,
  SessionUser
} from './types';

const ALL_PERMISSION_KEYS = [
  'BIND_NICKNAME',
  'AUTHORIZE_LOGIN',
  'VIEW_PROFILE',
  'SEND_GLOBAL_NOTIFICATION',
  'CREATE_INVITES',
  'VIEW_SERVER_STATS',
  'FORCE_BIND_IP',
  'VIEW_PLAYER_OR_CODE_INFO',
  'BAN_BY_CODE',
  'FORCE_CHANGE_NICKNAME',
  'SERVER_CONSOLE',
  'PRIORITY_SPEED'
] as const;

type SectionId =
  | 'overview'
  | 'skin'
  | 'pending-auth'
  | 'notifications'
  | 'statistics'
  | 'servers'
  | 'invite-create'
  | 'invite-check'
  | 'invite-list'
  | 'players'
  | 'admin-pending'
  | 'broadcast'
  | 'command-queue'
  | 'audit'
  | 'errors';

type IconName =
  | 'alertTriangle'
  | 'bell'
  | 'barChart'
  | 'calendar'
  | 'calendarCheck'
  | 'checkCircle'
  | 'chevronLeft'
  | 'chevronRight'
  | 'clipboard'
  | 'edit'
  | 'empty'
  | 'gear'
  | 'home'
  | 'key'
  | 'lock'
  | 'megaphone'
  | 'monitor'
  | 'paperclip'
  | 'plusCircle'
  | 'refresh'
  | 'search'
  | 'scroll'
  | 'shieldCheck'
  | 'telegram'
  | 'terminal'
  | 'ticket'
  | 'trash'
  | 'userCircle'
  | 'users';

const MAX_INT_UNIX_SECONDS = 2_147_483_647;
const IMMEDIATE_UNIX_MILLIS = 1;
const MAX_NICKNAME_SUGGESTIONS = 8;
const MAX_CODE_CHECK_INPUTS = 100;
const LIST_PAGE_SIZE = 50;
const LIST_PAGE_FETCH_LIMIT = LIST_PAGE_SIZE + 1;
const MAX_NOTIFICATION_ATTACHMENT_COUNT = 5;
const MAX_NOTIFICATION_ATTACHMENT_BYTES = 100 * 1024 * 1024;
const SELECTED_SERVER_STORAGE_PREFIX = 'eternal-verifier:selected-server:';
const SELECTED_SECTION_STORAGE_PREFIX = 'eternal-verifier:selected-section:';
const ALL_SERVERS_STORAGE_VALUE = 'all';
const LOGIN_LOCKED_UNTIL_STORAGE_KEY = 'eternal-verifier:login-locked-until';
const SELECTED_SECTION_TTL_MS = 15 * 60 * 1000;
const TELEGRAM_BOT_URL = 'https://t.me/eternalverifier_bot';
const TRUSTED_SKIN_TEXTURE_HOST = 'textures.minecraft.net';
const nicknameCache = new Map<string, string[]>();

function readLoginCodeQueryParam(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const value = new URL(window.location.href).searchParams.get('code')?.trim() ?? '';
    return value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

function clearLoginCodeQueryParam(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('code')) {
      return;
    }

    url.searchParams.delete('code');
    window.history.replaceState(window.history.state, document.title, url.toString());
  } catch {
    // Leaving the query parameter in place is harmless if History API is unavailable.
  }
}

function buildDirectLoginUrl(code: string): string {
  const encodedCode = encodeURIComponent(code);
  if (typeof window === 'undefined') {
    return `/?code=${encodedCode}`;
  }

  return `${window.location.origin}/?code=${encodedCode}`;
}

type LoadOptions = {
  showLoading?: boolean;
};

interface ApiInfo {
  message: string;
  tone: 'success' | 'error';
}

interface DashboardSection {
  id: SectionId;
  label: string;
  icon: IconName;
  visible: boolean;
}

interface DatePresetOption {
  id: string;
  label: string;
  value: string;
}

type SkinSourceType = 'NICKNAME' | 'URL' | 'PROPERTY';
type SkinVariant = 'CLASSIC' | 'SLIM';
type SkinAtlasHeight = 32 | 64;

type SkinFaceName = 'front' | 'back' | 'left' | 'right' | 'top' | 'bottom';

interface SkinFace {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface SkinCuboid {
  className: string;
  left: number;
  top: number;
  width: number;
  height: number;
  depth: number;
  layer?: boolean;
  legacyHeadLayer?: boolean;
  modernOnly?: boolean;
  slimLeft?: number;
  slimWidth?: number;
  faces: Record<SkinFaceName, SkinFace>;
  slimFaces?: Record<SkinFaceName, SkinFace>;
  legacyFaces?: Record<SkinFaceName, SkinFace>;
}

type CssVars = CSSProperties & Record<`--${string}`, string>;

interface SkinTextureDimensions {
  width: number;
  height: number;
}

interface SkinTextureInfo {
  dimensions: SkinTextureDimensions | null;
  legacyHeadLayerVisible: boolean;
}

const defaultSkinTextureInfo: SkinTextureInfo = {
  dimensions: null,
  legacyHeadLayerVisible: true
};

const skinTextureInfoCache = new Map<string, SkinTextureInfo>();

function skinAtlasHeightFromDimensions(dimensions: SkinTextureDimensions | null): SkinAtlasHeight {
  if (!dimensions || dimensions.height <= 0) {
    return 64;
  }

  return dimensions.width / dimensions.height >= 1.75 ? 32 : 64;
}

function detectLegacyHeadLayerVisible(image: HTMLImageElement, atlasHeight: SkinAtlasHeight): boolean {
  if (atlasHeight !== 32 || image.naturalWidth < 64 || image.naturalHeight < 16) {
    return true;
  }

  try {
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      return true;
    }

    context.drawImage(image, 0, 0);
    const pixels = context.getImageData(32, 0, 32, 16).data;
    for (let index = 3; index < pixels.length; index += 4) {
      if (pixels[index] < 255) {
        return true;
      }
    }
    return false;
  } catch {
    return true;
  }
}

function useSkinTextureInfo(textureUrl: string | null): SkinTextureInfo {
  const [info, setInfo] = useState<SkinTextureInfo>(() =>
    textureUrl ? skinTextureInfoCache.get(textureUrl) ?? defaultSkinTextureInfo : defaultSkinTextureInfo
  );

  useEffect(() => {
    if (!textureUrl) {
      setInfo(defaultSkinTextureInfo);
      return;
    }

    const cachedInfo = skinTextureInfoCache.get(textureUrl);
    if (cachedInfo) {
      setInfo(cachedInfo);
      return;
    }

    setInfo(defaultSkinTextureInfo);
    let active = true;
    const image = new Image();
    image.crossOrigin = 'anonymous';

    image.onload = () => {
      const dimensions = {
        width: image.naturalWidth,
        height: image.naturalHeight
      };
      const nextInfo = {
        dimensions,
        legacyHeadLayerVisible: detectLegacyHeadLayerVisible(image, skinAtlasHeightFromDimensions(dimensions))
      };
      skinTextureInfoCache.set(textureUrl, nextInfo);
      if (active) {
        setInfo(nextInfo);
      }
    };

    image.onerror = () => {
      if (active) {
        setInfo(defaultSkinTextureInfo);
      }
    };

    image.src = textureUrl;

    return () => {
      active = false;
      image.onload = null;
      image.onerror = null;
    };
  }, [textureUrl]);

  return info;
}

function keepSuccessInfo(current: ApiInfo | null): ApiInfo | null {
  return current?.tone === 'success' ? current : null;
}

function normalizeNickname(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function splitCodeCheckInput(value: string): string[] {
  return value
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeTextureUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const url = new URL(trimmed);
    if (url.hostname.toLowerCase() !== TRUSTED_SKIN_TEXTURE_HOST) {
      return null;
    }
    const path = url.pathname.replace(/^\/+/, '');
    if (!/^texture\/[A-Za-z0-9._-]{16,256}$/.test(path)) {
      return null;
    }
    return `https://${TRUSTED_SKIN_TEXTURE_HOST}/${path}`;
  } catch {
    return null;
  }
}

function decodeBase64Json(value: string): unknown {
  const normalized = value.trim().replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  const decoded = atob(padded);
  const bytes = Uint8Array.from(decoded, (char) => char.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

function textureUrlFromSkinValue(value: string): string | null {
  try {
    const parsed = decodeBase64Json(value);
    if (typeof parsed !== 'object' || parsed == null) {
      return null;
    }
    const textures = (parsed as Record<string, unknown>).textures;
    if (typeof textures !== 'object' || textures == null) {
      return null;
    }
    const skin = (textures as Record<string, unknown>).SKIN;
    if (typeof skin !== 'object' || skin == null) {
      return null;
    }
    const rawUrl = (skin as Record<string, unknown>).url;
    return typeof rawUrl === 'string' ? normalizeTextureUrl(rawUrl) : null;
  } catch {
    return null;
  }
}

function skinVariantFromSkinValue(value: string): SkinVariant | null {
  try {
    const parsed = decodeBase64Json(value);
    if (typeof parsed !== 'object' || parsed == null) {
      return null;
    }
    const textures = (parsed as Record<string, unknown>).textures;
    if (typeof textures !== 'object' || textures == null) {
      return null;
    }
    const skin = (textures as Record<string, unknown>).SKIN;
    if (typeof skin !== 'object' || skin == null) {
      return null;
    }
    const metadata = (skin as Record<string, unknown>).metadata;
    if (typeof metadata !== 'object' || metadata == null) {
      return null;
    }
    return (metadata as Record<string, unknown>).model === 'slim' ? 'SLIM' : null;
  } catch {
    return null;
  }
}

function getCachedNicknames(cacheKey: string): string[] {
  return nicknameCache.get(cacheKey) ?? [];
}

function mergeCachedNicknames(cacheKey: string, nextValues: Array<string | null | undefined>): string[] {
  const deduplicated = new Map<string, string>();

  for (const value of getCachedNicknames(cacheKey)) {
    deduplicated.set(value.toLowerCase(), value);
  }

  for (const rawValue of nextValues) {
    const normalized = normalizeNickname(rawValue);
    if (!normalized) {
      continue;
    }
    const key = normalized.toLowerCase();
    if (!deduplicated.has(key)) {
      deduplicated.set(key, normalized);
    }
  }

  const merged = Array.from(deduplicated.values()).sort((left, right) =>
    left.localeCompare(right, undefined, { sensitivity: 'base' })
  );
  nicknameCache.set(cacheKey, merged);
  return merged;
}

function buildNicknameSuggestions(cachedNicknames: string[], value: string): string[] {
  const query = value.trim().toLowerCase();
  if (!query) {
    return cachedNicknames.slice(0, MAX_NICKNAME_SUGGESTIONS);
  }

  return cachedNicknames
    .filter((nickname) => nickname.toLowerCase().startsWith(query))
    .slice(0, MAX_NICKNAME_SUGGESTIONS);
}

function appendPaginationParams(
  query: URLSearchParams,
  page: number,
  limitKey = 'limit',
  offsetKey = 'offset'
): void {
  query.set(limitKey, String(LIST_PAGE_FETCH_LIMIT));
  query.set(offsetKey, String(Math.max(0, page - 1) * LIST_PAGE_SIZE));
}

function pageItems<T>(items: T[]): T[] {
  return items.slice(0, LIST_PAGE_SIZE);
}

function hasNextPage<T>(items: T[]): boolean {
  return items.length > LIST_PAGE_SIZE;
}

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

function SvgIcon({ name, className }: { name: IconName; className?: string }) {
  const strokeProps = {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const
  };

  const iconBody: Record<IconName, ReactNode> = {
    alertTriangle: (
      <>
        <path {...strokeProps} d="M10.3 4.4 2.6 17.7A2.1 2.1 0 0 0 4.4 21h15.2a2.1 2.1 0 0 0 1.8-3.3L13.7 4.4a2.1 2.1 0 0 0-3.4 0Z" />
        <path {...strokeProps} d="M12 9v4" />
        <path {...strokeProps} d="M12 17h.01" />
      </>
    ),
    barChart: (
      <>
        <path {...strokeProps} d="M4 20V10" />
        <path {...strokeProps} d="M10 20V4" />
        <path {...strokeProps} d="M16 20v-7" />
        <path {...strokeProps} d="M22 20H2" />
      </>
    ),
    bell: (
      <>
        <path {...strokeProps} d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
        <path {...strokeProps} d="M10 21a2 2 0 0 0 4 0" />
      </>
    ),
    calendar: (
      <>
        <path {...strokeProps} d="M8 2v4" />
        <path {...strokeProps} d="M16 2v4" />
        <path {...strokeProps} d="M3.5 9h17" />
        <rect {...strokeProps} x="4" y="5" width="16" height="16" rx="3" />
      </>
    ),
    calendarCheck: (
      <>
        <path {...strokeProps} d="M8 2v4" />
        <path {...strokeProps} d="M16 2v4" />
        <path {...strokeProps} d="M3.5 9h17" />
        <rect {...strokeProps} x="4" y="5" width="16" height="16" rx="3" />
        <path {...strokeProps} d="m8.5 15 2.2 2.2 4.8-5" />
      </>
    ),
    checkCircle: (
      <>
        <path {...strokeProps} d="M22 11.1V12a10 10 0 1 1-5.9-9.1" />
        <path {...strokeProps} d="m9 11 3 3L22 4" />
      </>
    ),
    chevronLeft: <path {...strokeProps} d="m15 18-6-6 6-6" />,
    chevronRight: <path {...strokeProps} d="m9 18 6-6-6-6" />,
    clipboard: (
      <>
        <rect {...strokeProps} x="8" y="2" width="8" height="4" rx="1" />
        <path {...strokeProps} d="M9 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-3" />
        <path {...strokeProps} d="M8 12h8" />
        <path {...strokeProps} d="M8 16h6" />
      </>
    ),
    edit: (
      <>
        <path {...strokeProps} d="M12 20h9" />
        <path {...strokeProps} d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5Z" />
      </>
    ),
    empty: (
      <>
        <path {...strokeProps} d="M3 7.5 12 3l9 4.5-9 4.5L3 7.5Z" />
        <path {...strokeProps} d="M3 7.5v9L12 21l9-4.5v-9" />
        <path {...strokeProps} d="M12 12v9" />
      </>
    ),
    gear: (
      <>
        <path {...strokeProps} d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z" />
        <path {...strokeProps} d="M3 12h2.2" />
        <path {...strokeProps} d="M18.8 12H21" />
        <path {...strokeProps} d="m4.6 4.6 1.6 1.6" />
        <path {...strokeProps} d="m17.8 17.8 1.6 1.6" />
        <path {...strokeProps} d="M12 3v2.2" />
        <path {...strokeProps} d="M12 18.8V21" />
        <path {...strokeProps} d="m19.4 4.6-1.6 1.6" />
        <path {...strokeProps} d="m6.2 17.8-1.6 1.6" />
      </>
    ),
    home: (
      <>
        <path {...strokeProps} d="m3 11 9-8 9 8" />
        <path {...strokeProps} d="M5 10v10h14V10" />
        <path {...strokeProps} d="M9 20v-6h6v6" />
      </>
    ),
    key: (
      <>
        <circle {...strokeProps} cx="7.5" cy="14.5" r="4.5" />
        <path {...strokeProps} d="m11 11 9-9" />
        <path {...strokeProps} d="m15 6 3 3" />
        <path {...strokeProps} d="m17 4 3 3" />
      </>
    ),
    lock: (
      <>
        <rect {...strokeProps} x="5" y="11" width="14" height="10" rx="2" />
        <path {...strokeProps} d="M8 11V8a4 4 0 0 1 8 0v3" />
      </>
    ),
    megaphone: (
      <>
        <path {...strokeProps} d="M4 13h3l10 5V6L7 11H4a2 2 0 0 0 0 2Z" />
        <path {...strokeProps} d="m7 13 2 7" />
        <path {...strokeProps} d="M19 9a4 4 0 0 1 0 6" />
      </>
    ),
    monitor: (
      <>
        <rect {...strokeProps} x="3" y="4" width="18" height="12" rx="2" />
        <path {...strokeProps} d="M8 20h8" />
        <path {...strokeProps} d="M12 16v4" />
      </>
    ),
    paperclip: (
      <>
        <path {...strokeProps} d="m21.4 11.6-8.8 8.8a6 6 0 0 1-8.5-8.5l9.2-9.2a4 4 0 0 1 5.7 5.7l-9.2 9.2a2 2 0 0 1-2.8-2.8l8.5-8.5" />
      </>
    ),
    plusCircle: (
      <>
        <circle {...strokeProps} cx="12" cy="12" r="9" />
        <path {...strokeProps} d="M12 8v8" />
        <path {...strokeProps} d="M8 12h8" />
      </>
    ),
    refresh: (
      <>
        <path {...strokeProps} d="M20 11a8 8 0 0 0-14.5-4.7L4 8" />
        <path {...strokeProps} d="M4 4v4h4" />
        <path {...strokeProps} d="M4 13a8 8 0 0 0 14.5 4.7L20 16" />
        <path {...strokeProps} d="M20 20v-4h-4" />
      </>
    ),
    search: (
      <>
        <circle {...strokeProps} cx="11" cy="11" r="7" />
        <path {...strokeProps} d="m20 20-4-4" />
      </>
    ),
    scroll: (
      <>
        <path {...strokeProps} d="M8 4h10a3 3 0 0 1 3 3v13H8V4Z" />
        <path {...strokeProps} d="M8 20H5a3 3 0 0 1-3-3V6a2 2 0 0 1 4 0v11a3 3 0 0 0 3 3" />
        <path {...strokeProps} d="M11 9h6" />
        <path {...strokeProps} d="M11 13h6" />
      </>
    ),
    shieldCheck: (
      <>
        <path {...strokeProps} d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
        <path {...strokeProps} d="m9 12 2 2 4-5" />
      </>
    ),
    telegram: <path fill="currentColor" d="M21.9 4.3 18.7 19.2c-.2 1.1-.8 1.4-1.7.9l-4.6-3.4-2.2 2.1c-.2.2-.4.4-.9.4l.3-4.7 8.5-7.7c.4-.3-.1-.5-.6-.2L7 13.2 2.4 11.8c-1-.3-1-1 0-1.4L20.2 3.5c.8-.3 1.6.2 1.7.8Z" />,
    terminal: (
      <>
        <path {...strokeProps} d="m4 7 5 5-5 5" />
        <path {...strokeProps} d="M12 19h8" />
      </>
    ),
    ticket: (
      <>
        <path {...strokeProps} d="M4 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2a3 3 0 0 0 0 6v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2a3 3 0 0 0 0-6V7Z" />
        <path {...strokeProps} d="M13 5v14" />
      </>
    ),
    trash: (
      <>
        <path {...strokeProps} d="M3 6h18" />
        <path {...strokeProps} d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        <path {...strokeProps} d="M19 6 18 20a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path {...strokeProps} d="M10 11v6" />
        <path {...strokeProps} d="M14 11v6" />
      </>
    ),
    userCircle: (
      <>
        <circle {...strokeProps} cx="12" cy="12" r="10" />
        <circle {...strokeProps} cx="12" cy="10" r="3" />
        <path {...strokeProps} d="M6.5 18a6.5 6.5 0 0 1 11 0" />
      </>
    ),
    users: (
      <>
        <path {...strokeProps} d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle {...strokeProps} cx="9" cy="7" r="4" />
        <path {...strokeProps} d="M22 21v-2a4 4 0 0 0-3-3.9" />
        <path {...strokeProps} d="M16 3.1a4 4 0 0 1 0 7.8" />
      </>
    )
  };

  return (
    <svg className={cx('ui-icon', className)} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {iconBody[name]}
    </svg>
  );
}

function TelegramIcon() {
  return <SvgIcon name="telegram" className="telegram-icon" />;
}

function TelegramLinkButton({ className }: { className?: string }) {
  return (
    <a
      className={cx('telegram-link', className)}
      href={TELEGRAM_BOT_URL}
      target="_blank"
      rel="noreferrer"
    >
      <TelegramIcon />
      <span>Telegram</span>
    </a>
  );
}

function hasPermission(user: SessionUser, permission: string): boolean {
  return user.isGlobalMaster || user.permissions.includes(permission);
}

function hasAnyPermission(user: SessionUser, permissions: readonly string[]): boolean {
  return user.isGlobalMaster || permissions.some((permission) => user.permissions.includes(permission));
}

function selectedServerStorageKey(user: SessionUser): string {
  return `${SELECTED_SERVER_STORAGE_PREFIX}${user.inviteCodeId}`;
}

function selectedSectionStorageKey(user: SessionUser): string {
  return `${SELECTED_SECTION_STORAGE_PREFIX}${user.inviteCodeId}`;
}

function isSectionId(value: unknown): value is SectionId {
  return (
    value === 'overview' ||
    value === 'skin' ||
    value === 'pending-auth' ||
    value === 'notifications' ||
    value === 'statistics' ||
    value === 'servers' ||
    value === 'invite-create' ||
    value === 'invite-check' ||
    value === 'invite-list' ||
    value === 'players' ||
    value === 'admin-pending' ||
    value === 'broadcast' ||
    value === 'command-queue' ||
    value === 'audit' ||
    value === 'errors'
  );
}

function readStoredSelectedSection(user: SessionUser): SectionId | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    const rawValue = window.localStorage.getItem(selectedSectionStorageKey(user));
    if (!rawValue) {
      return undefined;
    }

    const parsed = JSON.parse(rawValue) as { sectionId?: unknown; expiresAt?: unknown };
    const expiresAt = typeof parsed.expiresAt === 'number' ? parsed.expiresAt : 0;
    if (expiresAt > Date.now() && parsed.sectionId === 'invites') {
      return 'invite-list';
    }
    if (expiresAt <= Date.now() || !isSectionId(parsed.sectionId)) {
      window.localStorage.removeItem(selectedSectionStorageKey(user));
      return undefined;
    }

    return parsed.sectionId;
  } catch {
    try {
      window.localStorage.removeItem(selectedSectionStorageKey(user));
    } catch {
      // Clearing a malformed UI preference is best-effort only.
    }
    return undefined;
  }
}

function storeSelectedSection(user: SessionUser, sectionId: SectionId): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      selectedSectionStorageKey(user),
      JSON.stringify({
        sectionId,
        expiresAt: Date.now() + SELECTED_SECTION_TTL_MS
      })
    );
  } catch {
    // Persisting the UI preference is best-effort only.
  }
}

function defaultSelectedServerId(user: SessionUser): number | null {
  return user.serverId ?? user.accessibleServers[0]?.id ?? null;
}

function normalizeSelectedServerId(user: SessionUser, serverId: number | null): number | null {
  if (user.serverId != null) {
    return user.serverId;
  }

  if (serverId == null) {
    return user.isGlobalMaster ? null : defaultSelectedServerId(user);
  }

  return user.accessibleServers.some((server) => server.id === serverId)
    ? serverId
    : defaultSelectedServerId(user);
}

function readStoredSelectedServerId(user: SessionUser): number | null | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    const storedValue = window.localStorage.getItem(selectedServerStorageKey(user));
    if (!storedValue) {
      return undefined;
    }
    if (storedValue === ALL_SERVERS_STORAGE_VALUE) {
      return null;
    }

    const parsedValue = Number(storedValue);
    return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : undefined;
  } catch {
    return undefined;
  }
}

function resolveInitialSelectedServerId(user: SessionUser): number | null {
  const storedServerId = readStoredSelectedServerId(user);
  return normalizeSelectedServerId(
    user,
    storedServerId === undefined ? defaultSelectedServerId(user) : storedServerId
  );
}

function storeSelectedServerId(user: SessionUser, serverId: number | null): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(
      selectedServerStorageKey(user),
      serverId == null ? ALL_SERVERS_STORAGE_VALUE : String(serverId)
    );
  } catch {
    // Persisting the UI preference is best-effort only.
  }
}

function clearStoredLoginLockedUntil(): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(LOGIN_LOCKED_UNTIL_STORAGE_KEY);
  } catch {
    // Persisting the UI cooldown is best-effort only.
  }
}

function readStoredLoginLockedUntil(): number | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(LOGIN_LOCKED_UNTIL_STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    const lockedUntil = Number(rawValue);
    if (Number.isFinite(lockedUntil) && lockedUntil > Date.now()) {
      return lockedUntil;
    }

    window.localStorage.removeItem(LOGIN_LOCKED_UNTIL_STORAGE_KEY);
  } catch {
    return null;
  }

  return null;
}

function storeLoginLockedUntil(lockedUntil: number): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(LOGIN_LOCKED_UNTIL_STORAGE_KEY, String(lockedUntil));
  } catch {
    // Persisting the UI cooldown is best-effort only.
  }
}

function permissionDescription(locale: Locale, permission: string): string {
  const descriptions: Record<string, { ru: string; en: string }> = {
    BIND_NICKNAME: {
      ru: 'Позволяет привязать никнейм игрока к профилю, чтобы вход и действия были закреплены за одним именем.',
      en: 'Allows binding a player nickname to the profile so logins and actions stay tied to one name.'
    },
    AUTHORIZE_LOGIN: {
      ru: 'Даёт доступ к подтверждению или отклонению входов с новых IP-адресов.',
      en: 'Allows approving or rejecting login attempts from new IP addresses.'
    },
    VIEW_PROFILE: {
      ru: 'Открывает доступ к просмотру профиля, основных данных и истории активности.',
      en: 'Grants access to profile details, core account data, and activity history.'
    },
    SEND_GLOBAL_NOTIFICATION: {
      ru: 'Позволяет отправлять уведомления игрокам по серверу или глобально по системе.',
      en: 'Allows sending notifications to players within a server or globally across the system.'
    },
    CREATE_INVITES: {
      ru: 'Разрешает создавать новые коды доступа и выдавать им выбранные права.',
      en: 'Allows generating new invite codes and assigning selected permissions to them.'
    },
    VIEW_SERVER_STATS: {
      ru: 'Открывает статистику серверов и общие показатели по кодам, IP и игрокам.',
      en: 'Shows server statistics and aggregate metrics for codes, IPs, and players.'
    },
    FORCE_BIND_IP: {
      ru: 'Позволяет вручную привязать IP игроку или полностью сбросить список авторизованных IP.',
      en: 'Allows manually binding an IP to a player or fully resetting authorized IP addresses.'
    },
    VIEW_PLAYER_OR_CODE_INFO: {
      ru: 'Даёт доступ к поиску игроков и кодов, а также к просмотру их подробной информации.',
      en: 'Allows searching players and invite codes and viewing their detailed information.'
    },
    BAN_BY_CODE: {
      ru: 'Позволяет заблокировать код, отозвать его сессии и при необходимости кикнуть игрока.',
      en: 'Allows banning an invite code, revoking its sessions, and optionally kicking the player.'
    },
    FORCE_CHANGE_NICKNAME: {
      ru: 'Даёт право принудительно изменить привязанный никнейм игрока.',
      en: 'Allows forcibly changing the player nickname bound to the profile.'
    },
    SERVER_CONSOLE: {
      ru: 'Открывает очередь консольных команд сервера и контроль их выполнения.',
      en: 'Grants access to the server console command queue and execution monitoring.'
    },
    PRIORITY_SPEED: {
      ru: 'Переключает сайт на более приоритетный способ подключения к базе через Hyperdrive.',
      en: 'Switches the website to the higher-priority Hyperdrive database transport.'
    }
  };

  return descriptions[permission]?.[locale] ?? permissionLabel(locale, permission);
}

function formatDate(locale: Locale, value: string | null | undefined): string {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(locale === 'ru' ? 'ru-RU' : 'en-US', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
}

function formatFileSize(locale: Locale, bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return 'вЂ”';
  }

  const formatter = new Intl.NumberFormat(locale === 'ru' ? 'ru-RU' : 'en-US', {
    maximumFractionDigits: bytes >= 1024 * 1024 ? 1 : 0
  });

  if (bytes >= 1024 * 1024) {
    return `${formatter.format(bytes / (1024 * 1024))} MB`;
  }
  if (bytes >= 1024) {
    return `${formatter.format(bytes / 1024)} KB`;
  }
  return `${formatter.format(bytes)} B`;
}

function maskIpAddress(ipAddress: string | null | undefined): string | null {
  const trimmed = ipAddress?.trim();
  if (!trimmed) {
    return null;
  }

  if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(trimmed)) {
    return trimmed.replace(/\.\d{1,3}$/, '.*');
  }

  const lastColonIndex = trimmed.lastIndexOf(':');
  if (lastColonIndex >= 0) {
    return `${trimmed.slice(0, lastColonIndex + 1)}*`;
  }

  return trimmed.replace(/\d+$/, '*');
}

function toDateTimeLocal(value: Date): string {
  const local = new Date(value.getTime() - value.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function parseDateTimeLocalValue(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }

  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const [, year, month, day, hour, minute] = match;
  const parsed = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    0,
    0
  );

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function calendarWeekStart(locale: Locale): 0 | 1 {
  return locale === 'ru' ? 1 : 0;
}

function shiftCalendarDay(date: Date, amount: number): Date {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() + amount,
    date.getHours(),
    date.getMinutes(),
    0,
    0
  );
}

function shiftCalendarMonth(date: Date, amount: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1, date.getHours(), date.getMinutes(), 0, 0);
}

function isSameCalendarDay(left: Date | null, right: Date | null): boolean {
  if (!left || !right) {
    return false;
  }

  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

function buildCalendarDays(viewDate: Date, locale: Locale): Date[] {
  const weekStart = calendarWeekStart(locale);
  const firstMonthDay = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const leadingDays = (firstMonthDay.getDay() - weekStart + 7) % 7;
  const gridStart = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1 - leadingDays);

  return Array.from({ length: 42 }, (_, index) => shiftCalendarDay(gridStart, index));
}

function calendarWeekdayLabels(locale: Locale): string[] {
  const formatter = new Intl.DateTimeFormat(locale === 'ru' ? 'ru-RU' : 'en-US', { weekday: 'short' });
  const weekStart = calendarWeekStart(locale);
  const baseSunday = new Date(2024, 0, 7);

  return Array.from({ length: 7 }, (_, index) =>
    formatter.format(shiftCalendarDay(baseSunday, (index + weekStart) % 7))
  );
}

function badgeClass(kind: string): string {
  const normalized = kind.toUpperCase();
  if (normalized.includes('ERROR') || normalized.includes('FAILED') || normalized.includes('REJECT') || normalized.includes('REVOK')) {
    return 'badge badge-error';
  }
  if (normalized.includes('WARNING') || normalized.includes('PENDING') || normalized.includes('PROCESS')) {
    return 'badge badge-warning';
  }
  if (normalized.includes('SUCCESS') || normalized.includes('APPROVED') || normalized.includes('DONE') || normalized.includes('ACTIVE')) {
    return 'badge badge-success';
  }
  return 'badge badge-muted';
}

function statusTextClass(kind: string | null): string {
  const normalized = kind?.toUpperCase() ?? '';
  if (normalized.includes('ERROR') || normalized.includes('FAILED') || normalized.includes('REJECT') || normalized.includes('REVOK')) {
    return 'status-text status-text-error';
  }
  if (normalized.includes('WARNING') || normalized.includes('PENDING') || normalized.includes('PROCESS')) {
    return 'status-text status-text-warning';
  }
  if (normalized.includes('SUCCESS') || normalized.includes('APPROVED') || normalized.includes('DONE') || normalized.includes('ACTIVE')) {
    return 'status-text status-text-success';
  }
  return 'status-text status-text-muted';
}

function humanStatus(t: ReturnType<typeof useI18n>['t'], status: string): string {
  const key = `requestStatus${status.charAt(0)}${status.slice(1).toLowerCase()}`;
  const queueKey = `queueStatus${status.charAt(0)}${status.slice(1).toLowerCase()}`;
  const translationMap: Record<string, string> = {
    requestStatusPending: t('requestStatusPending'),
    requestStatusApproved: t('requestStatusApproved'),
    requestStatusRejected: t('requestStatusRejected'),
    requestStatusExpired: t('requestStatusExpired'),
    requestStatusCancelled: t('requestStatusCancelled'),
    queueStatusPending: t('queueStatusPending'),
    queueStatusProcessing: t('queueStatusProcessing'),
    queueStatusDone: t('queueStatusDone'),
    queueStatusFailed: t('queueStatusFailed'),
    queueStatusCancelled: t('queueStatusCancelled')
  };

  return translationMap[key] ?? translationMap[queueKey] ?? status;
}

function copyText(value: string): void {
  void navigator.clipboard?.writeText(value);
}

function localeLabel(locale: Locale, t: ReturnType<typeof useI18n>['t']): string {
  if (locale === 'ru') {
    return `RU ${t('russian')}`;
  }
  return `EN ${t('english')}`;
}

function sectionIcon(id: SectionId): IconName {
  switch (id) {
    case 'overview':
      return 'home';
    case 'skin':
      return 'userCircle';
    case 'pending-auth':
      return 'lock';
    case 'notifications':
      return 'bell';
    case 'statistics':
      return 'barChart';
    case 'servers':
      return 'monitor';
    case 'invite-create':
      return 'plusCircle';
    case 'invite-check':
      return 'search';
    case 'invite-list':
      return 'ticket';
    case 'players':
      return 'users';
    case 'admin-pending':
      return 'clipboard';
    case 'broadcast':
      return 'megaphone';
    case 'command-queue':
      return 'gear';
    case 'audit':
      return 'scroll';
    case 'errors':
      return 'alertTriangle';
    default:
      return 'home';
  }
}

function sectionDescription(id: SectionId, t: ReturnType<typeof useI18n>['t']): string {
  switch (id) {
    case 'overview':
      return t('sectionDescOverview');
    case 'skin':
      return t('sectionDescSkin');
    case 'pending-auth':
      return t('sectionDescPendingAuth');
    case 'notifications':
      return t('sectionDescNotifications');
    case 'statistics':
      return t('sectionDescStatistics');
    case 'servers':
      return t('sectionDescServers');
    case 'invite-create':
      return t('sectionDescInviteCreate');
    case 'invite-check':
      return t('sectionDescInviteCheck');
    case 'invite-list':
      return t('sectionDescInviteList');
    case 'players':
      return t('sectionDescPlayers');
    case 'admin-pending':
      return t('sectionDescAdminPending');
    case 'broadcast':
      return t('sectionDescBroadcast');
    case 'command-queue':
      return t('sectionDescCommandQueue');
    case 'audit':
      return t('sectionDescAudit');
    case 'errors':
      return t('sectionDescErrors');
    default:
      return '';
  }
}

function AppShell() {
  const [session, setSession] = useState<SessionPayload | null>(null);
  const [loading, setLoading] = useState(true);

  const loadSession = async () => {
    const payload = await api.get<SessionPayload>('/api/auth/session');
    setSession(payload);
    return payload;
  };

  useEffect(() => {
    void loadSession()
      .catch((error) => {
        console.error('Failed to load session', error);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading || !session) {
    return <LoadingScreen defaultLocale="ru" />;
  }

  return (
    <I18nProvider key={session.app.defaultLocale} defaultLocale={session.app.defaultLocale}>
      {session.authenticated && session.user ? (
        <DashboardApp session={session} onSessionChange={setSession} onRefreshSession={loadSession} />
      ) : (
        <LoginScreen onLoggedIn={setSession} appName={session.app.name} defaultLocale={session.app.defaultLocale} />
      )}
    </I18nProvider>
  );
}

function LoadingScreen({ defaultLocale }: { defaultLocale: Locale }) {
  return (
    <I18nProvider defaultLocale={defaultLocale}>
      <LoadingScreenInner />
    </I18nProvider>
  );
}

function LoadingScreenInner() {
  const { t } = useI18n();
  return (
    <div className="app-shell app-shell-center">
      <div className="hero-card loading-card">
        <div className="spinner" />
        <h1>Eternal Verifier</h1>
        <p>{t('loading')}</p>
      </div>
    </div>
  );
}

function LoginScreen({
  onLoggedIn,
  appName,
  defaultLocale
}: {
  onLoggedIn: (session: SessionPayload) => void;
  appName: string;
  defaultLocale: Locale;
}) {
  return (
    <I18nProvider defaultLocale={defaultLocale}>
      <LoginScreenInner onLoggedIn={onLoggedIn} appName={appName} />
    </I18nProvider>
  );
}

function LoginScreenInner({
  onLoggedIn,
  appName
}: {
  onLoggedIn: (session: SessionPayload) => void;
  appName: string;
}) {
  const { locale, setLocale, t } = useI18n();
  const [step, setStep] = useState<'code' | 'password'>('code');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [requiresPasswordConfirmation, setRequiresPasswordConfirmation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lockedUntil, setLockedUntil] = useState<number | null>(() => readStoredLoginLockedUntil());
  const [remaining, setRemaining] = useState(0);
  const [autoLoginInProgress, setAutoLoginInProgress] = useState(() => readLoginCodeQueryParam() != null);
  const [directLinkPasswordMode, setDirectLinkPasswordMode] = useState(false);
  const autoLoginStartedRef = useRef(false);
  const passwordHint =
    locale === 'ru'
      ? 'Пароль должен содержать от 3 до 16 символов.'
      : 'Password must contain 3 to 16 characters.';
  const passwordPlaceholder = locale === 'ru' ? 'Пароль (3-16 символов)' : 'Password (3-16 characters)';
  const passwordConfirmationPlaceholder = locale === 'ru' ? 'Подтвердите пароль' : 'Confirm password';
  const continueLabel = locale === 'ru' ? 'Продолжить' : 'Continue';
  const backLabel = locale === 'ru' ? 'Назад' : 'Back';
  const signInLabel = t('loginButton');
  const directLinkPasswordTitle = locale === 'ru' ? '\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u043f\u0430\u0440\u043e\u043b\u044c' : 'Enter password';
  const directLinkPasswordDescription =
    locale === 'ru'
      ? '\u041a\u043e\u0434 \u0443\u0436\u0435 \u0443\u043a\u0430\u0437\u0430\u043d \u0432 \u0441\u0441\u044b\u043b\u043a\u0435.'
      : 'The code is already provided by the link.';

  const applyLoginCooldown = (retryAfterSeconds: number) => {
    const nextLockedUntil = Date.now() + retryAfterSeconds * 1_000;
    setLockedUntil(nextLockedUntil);
    storeLoginLockedUntil(nextLockedUntil);
  };

  useEffect(() => {
    if (!lockedUntil) {
      setRemaining(0);
      clearStoredLoginLockedUntil();
      return;
    }

    const update = () => {
      const next = Math.max(0, Math.ceil((lockedUntil - Date.now()) / 1000));
      setRemaining(next);
      if (next === 0) {
        setLockedUntil(null);
        clearStoredLoginLockedUntil();
      }
    };

    update();
    const timer = window.setInterval(update, 1_000);
    return () => window.clearInterval(timer);
  }, [lockedUntil]);

  const resetPasswordStepState = () => {
    setPassword('');
    setPasswordConfirmation('');
    setRequiresPasswordConfirmation(false);
  };

  const handleLoginError = (rawError: unknown) => {
    if (rawError instanceof ApiClientError) {
      setError(rawError.message);
      const retryAfter = Number((rawError.details as { retryAfterSeconds?: number } | null)?.retryAfterSeconds ?? 0);
      if (retryAfter > 0) {
        applyLoginCooldown(retryAfter);
      }
    } else {
      setError(rawError instanceof Error ? rawError.message : 'Request failed');
    }
  };

  const signInWithCodeOnly = async (codeValue: string) => {
    const payload = await api.post<SessionPayload>('/api/auth/login/code', { code: codeValue });
    clearStoredLoginLockedUntil();
    clearLoginCodeQueryParam();
    setLockedUntil(null);
    onLoggedIn(payload);
  };

  const continueWithCode = async (codeValue: string, source: 'manual' | 'direct-link'): Promise<boolean> => {
    const payload = await api.post<LoginPasswordStatePayload>('/api/auth/login/password-state', { code: codeValue });
    if (!payload.requiresPassword) {
      await signInWithCodeOnly(codeValue);
      return true;
    }

    setRequiresPasswordConfirmation(!payload.passwordConfigured);
    setPassword('');
    setPasswordConfirmation('');
    setStep('password');
    if (source === 'direct-link') {
      setDirectLinkPasswordMode(true);
    }
    return false;
  };

  useEffect(() => {
    if (autoLoginStartedRef.current) {
      return;
    }

    const directCode = readLoginCodeQueryParam();
    if (!directCode) {
      setAutoLoginInProgress(false);
      return;
    }

    autoLoginStartedRef.current = true;
    setCode(directCode);
    setSubmitting(true);
    setError(null);

    void continueWithCode(directCode, 'direct-link')
      .then((loggedIn) => {
        if (!loggedIn) {
          clearLoginCodeQueryParam();
        }
      })
      .catch((rawError) => {
        clearLoginCodeQueryParam();
        setDirectLinkPasswordMode(false);
        handleLoginError(rawError);
      })
      .finally(() => {
        setSubmitting(false);
        setAutoLoginInProgress(false);
      });
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting || remaining > 0) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      if (step === 'code') {
        setDirectLinkPasswordMode(false);
        await continueWithCode(code, 'manual');
        return;
      }

      const payload = await api.post<SessionPayload>('/api/auth/login', {
        code,
        password,
        passwordConfirmation: requiresPasswordConfirmation ? passwordConfirmation : null
      });
      clearStoredLoginLockedUntil();
      setLockedUntil(null);
      onLoggedIn(payload);
    } catch (rawError) {
      handleLoginError(rawError);
    } finally {
      setSubmitting(false);
    }
  };

  const goBackToCodeStep = () => {
    if (submitting) {
      return;
    }
    setStep('code');
    setError(null);
    setDirectLinkPasswordMode(false);
    resetPasswordStepState();
  };

  const codeValid = code.trim().length >= 1;
  const passwordValid = password.length >= 3 && password.length <= 16;
  const passwordConfirmationValid =
    !requiresPasswordConfirmation ||
    (passwordConfirmation.length >= 3 &&
      passwordConfirmation.length <= 16 &&
      passwordConfirmation === password);
  const submitDisabled =
    submitting ||
    remaining > 0 ||
    (step === 'code' ? !codeValid : !passwordValid || !passwordConfirmationValid);
  const hideCodeInput = directLinkPasswordMode && step === 'password';

  if (autoLoginInProgress) {
    return <LoadingScreenInner />;
  }

  return (
    <div className="app-shell app-shell-center">
      <div className="hero-card hero-card-wide login-card-intro">
        <div className="hero-topbar">
          <div>
            <div className="eyebrow">{appName}</div>
            <h1>{directLinkPasswordMode ? directLinkPasswordTitle : t('loginTitle')}</h1>
            <p>{directLinkPasswordMode ? directLinkPasswordDescription : t('loginDescription')}</p>
          </div>
          <div className="locale-switcher">
            <TelegramLinkButton />
            <select
              className="select-input locale-select"
              value={locale}
              onChange={(event) => setLocale(event.target.value as Locale)}
              aria-label={t('language')}
            >
              <option value="ru">{localeLabel('ru', t)}</option>
              <option value="en">{localeLabel('en', t)}</option>
            </select>
          </div>
        </div>

        <form className="stack-lg" onSubmit={handleSubmit}>
          {!hideCodeInput ? (
            <input
              className="text-input code-input code-input-intro"
              type="text"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              placeholder={t('loginPlaceholder')}
              value={code}
              onChange={(event) => setCode(event.target.value)}
              disabled={submitting || remaining > 0 || step === 'password'}
            />
          ) : null}

          {step === 'password' ? (
            <div className="stack-lg password-step-intro">
              <input
                className="text-input password-input-intro"
                type="password"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                placeholder={passwordPlaceholder}
                value={password}
                maxLength={16}
                onChange={(event) => setPassword(event.target.value.slice(0, 16))}
                disabled={submitting || remaining > 0}
              />
              {requiresPasswordConfirmation ? (
                <input
                  className="text-input password-confirmation-input-intro"
                  type="password"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder={passwordConfirmationPlaceholder}
                  value={passwordConfirmation}
                  maxLength={16}
                  onChange={(event) => setPasswordConfirmation(event.target.value.slice(0, 16))}
                  disabled={submitting || remaining > 0}
                />
              ) : null}
              <div className="subtle-text">{passwordHint}</div>
              <div className="button-row">
                {!directLinkPasswordMode ? (
                  <button type="button" className="ghost-button" onClick={goBackToCodeStep} disabled={submitting || remaining > 0}>
                    {backLabel}
                  </button>
                ) : null}
                <button className="primary-button large-button" disabled={submitDisabled}>
                  {submitting ? <ButtonLoading label={t('loading')} /> : signInLabel}
                </button>
              </div>
            </div>
          ) : (
            <button className="primary-button large-button" disabled={submitDisabled}>
              {submitting ? <ButtonLoading label={t('loading')} /> : continueLabel}
            </button>
          )}
        </form>

        {remaining > 0 || error ? (
          <div className="login-feedback">
            {remaining > 0 ? <div className="info-banner">{t('loginLocked', { seconds: remaining })}</div> : null}
            {error ? (
              <div className="alert alert-error">
                <SvgIcon name="alertTriangle" className="alert-icon" />
                {error}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="hero-footer">
          <span>{t('appSubtitle')}</span>
          <span>{t('loginPrompt')}</span>
        </div>
      </div>
    </div>
  );
}

function DashboardApp({
  session,
  onSessionChange,
  onRefreshSession
}: {
  session: SessionPayload;
  onSessionChange: (session: SessionPayload) => void;
  onRefreshSession: () => Promise<SessionPayload>;
}) {
  const { locale, setLocale, t } = useI18n();
  const user = session.user as SessionUser;
  const sections = useMemo<DashboardSection[]>(
    () => [
      { id: 'overview', label: t('overview'), icon: sectionIcon('overview'), visible: true },
      { id: 'skin', label: t('skin'), icon: sectionIcon('skin'), visible: hasPermission(user, 'BIND_NICKNAME') },
      { id: 'pending-auth', label: t('pendingAuth'), icon: sectionIcon('pending-auth'), visible: hasPermission(user, 'AUTHORIZE_LOGIN') },
      { id: 'notifications', label: t('notifications'), icon: sectionIcon('notifications'), visible: true },
      { id: 'statistics', label: t('serverStats'), icon: sectionIcon('statistics'), visible: user.isGlobalMaster || hasPermission(user, 'VIEW_SERVER_STATS') },
      { id: 'servers', label: t('servers'), icon: sectionIcon('servers'), visible: user.isGlobalMaster },
      {
        id: 'invite-create',
        label: t('codeCreation'),
        icon: sectionIcon('invite-create'),
        visible: hasPermission(user, 'CREATE_INVITES')
      },
      {
        id: 'invite-check',
        label: t('codeChecking'),
        icon: sectionIcon('invite-check'),
        visible: hasAnyPermission(user, ['CREATE_INVITES', 'VIEW_PLAYER_OR_CODE_INFO', 'BAN_BY_CODE'])
      },
      {
        id: 'invite-list',
        label: t('codeList'),
        icon: sectionIcon('invite-list'),
        visible: hasAnyPermission(user, ['CREATE_INVITES', 'VIEW_PLAYER_OR_CODE_INFO', 'BAN_BY_CODE'])
      },
      {
        id: 'players',
        label: t('players'),
        icon: sectionIcon('players'),
        visible: hasAnyPermission(user, ['VIEW_PLAYER_OR_CODE_INFO', 'FORCE_CHANGE_NICKNAME', 'FORCE_BIND_IP', 'BAN_BY_CODE'])
      },
      {
        id: 'admin-pending',
        label: t('adminPending'),
        icon: sectionIcon('admin-pending'),
        visible: hasPermission(user, 'VIEW_PLAYER_OR_CODE_INFO')
      },
      {
        id: 'broadcast',
        label: t('broadcast'),
        icon: sectionIcon('broadcast'),
        visible: hasPermission(user, 'SEND_GLOBAL_NOTIFICATION')
      },
      {
        id: 'command-queue',
        label: t('commandQueue'),
        icon: sectionIcon('command-queue'),
        visible: hasPermission(user, 'SERVER_CONSOLE')
      },
      {
        id: 'audit',
        label: t('audit'),
        icon: sectionIcon('audit'),
        visible: hasPermission(user, 'VIEW_PLAYER_OR_CODE_INFO')
      },
      {
        id: 'errors',
        label: t('errors'),
        icon: sectionIcon('errors'),
        visible: hasPermission(user, 'VIEW_PLAYER_OR_CODE_INFO')
      }
    ],
    [t, user]
  );

  const [activeSection, setActiveSection] = useState<SectionId>(() => {
    const storedSection = readStoredSelectedSection(user);
    if (storedSection && sections.some((section) => section.id === storedSection && section.visible)) {
      return storedSection;
    }

    return sections.find((section) => section.visible)?.id ?? 'overview';
  });
  const [selectedServerId, setSelectedServerId] = useState<number | null>(() => resolveInitialSelectedServerId(user));
  const [logoutBusy, setLogoutBusy] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [pendingAuthRequests, setPendingAuthRequests] = useState(0);
  const [pendingIpRequests, setPendingIpRequests] = useState(0);

  useEffect(() => {
    const visibleSection = sections.find((section) => section.id === activeSection && section.visible);
    if (!visibleSection) {
      const fallback = sections.find((section) => section.visible);
      if (fallback) {
        setActiveSection(fallback.id);
        storeSelectedSection(user, fallback.id);
      }
    }
  }, [activeSection, sections, user]);

  useEffect(() => {
    const normalizedServerId = normalizeSelectedServerId(user, selectedServerId);
    if (selectedServerId !== normalizedServerId) {
      setSelectedServerId(normalizedServerId);
    }
  }, [selectedServerId, user]);

  useEffect(() => {
    storeSelectedServerId(user, normalizeSelectedServerId(user, selectedServerId));
  }, [selectedServerId, user]);

  useEffect(() => {
    if (!hasPermission(user, 'AUTHORIZE_LOGIN')) {
      setPendingAuthRequests(0);
      return;
    }
    if (activeSection === 'pending-auth') {
      return;
    }

    let cancelled = false;
    let inFlight = false;
    const refreshPendingAuthCount = async () => {
      if (inFlight) {
        return;
      }

      inFlight = true;
      try {
        const payload = await api.get<{ count: number }>('/api/me/pending-auth/count');
        if (!cancelled) {
          setPendingAuthRequests(payload.count);
        }
      } catch {
        if (!cancelled) {
          setPendingAuthRequests(0);
        }
      } finally {
        inFlight = false;
      }
    };

    void refreshPendingAuthCount();
    const intervalId = window.setInterval(() => void refreshPendingAuthCount(), 2_000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [activeSection, user]);

  useEffect(() => {
    let cancelled = false;
    const refreshSidebarCounts = async () => {
      try {
        const dashboardQuery = new URLSearchParams();
        if (selectedServerId) {
          dashboardQuery.set('serverId', String(selectedServerId));
        }
        dashboardQuery.set('authorizedIpLimit', '1');
        dashboardQuery.set('includePendingAuthRequests', 'false');
        const payload = await api.get<DashboardData>(`/api/me/dashboard?${dashboardQuery.toString()}`);
        if (cancelled) {
          return;
        }
        setUnreadNotifications(payload.summary.unreadNotifications ?? 0);
      } catch {
        if (cancelled) {
          return;
        }
        setUnreadNotifications(0);
      }

      if (hasPermission(user, 'AUTHORIZE_LOGIN')) {
        try {
          const pendingAuthPayload = await api.get<{ count: number }>('/api/me/pending-auth/count');
          if (!cancelled) {
            setPendingAuthRequests(pendingAuthPayload.count);
          }
        } catch {
          if (!cancelled) {
            setPendingAuthRequests(0);
          }
        }
      } else if (!cancelled) {
        setPendingAuthRequests(0);
      }

      if (!hasPermission(user, 'VIEW_PLAYER_OR_CODE_INFO')) {
        if (!cancelled) {
          setPendingIpRequests(0);
        }
        return;
      }

      try {
        const pendingQuery = new URLSearchParams();
        pendingQuery.set('status', 'PENDING');
        pendingQuery.set('limit', '200');
        if (selectedServerId) {
          pendingQuery.set('serverId', String(selectedServerId));
        }
        const pendingPayload = await api.get<AdminPendingAuthItem[]>(`/api/admin/pending-auth?${pendingQuery.toString()}`);
        if (!cancelled) {
          setPendingIpRequests(pendingPayload.length);
        }
      } catch {
        if (!cancelled) {
          setPendingIpRequests(0);
        }
      }
    };

    void refreshSidebarCounts();
    return () => {
      cancelled = true;
    };
  }, [selectedServerId, user]);

  const handleLogout = async () => {
    if (logoutBusy) {
      return;
    }
    setLogoutBusy(true);
    try {
      await api.post('/api/auth/logout');
      onSessionChange({
        authenticated: false,
        app: session.app,
        user: null
      });
    } finally {
      setLogoutBusy(false);
    }
  };

  const renderSection = (): ReactNode => {
    switch (activeSection) {
      case 'overview':
        return (
          <OverviewSection
            user={user}
            selectedServerId={selectedServerId}
            onRefreshSession={async () => {
              const refreshed = await onRefreshSession();
              onSessionChange(refreshed);
            }}
          />
        );
      case 'skin':
        return <SkinSection user={user} selectedServerId={selectedServerId} />;
      case 'pending-auth':
        return <OwnPendingAuthSection onPendingCountChange={setPendingAuthRequests} />;
      case 'notifications':
        return (
          <NotificationsSection
            selectedServerId={selectedServerId}
            unreadCount={unreadNotifications}
            onUnreadCountChange={setUnreadNotifications}
          />
        );
      case 'statistics':
        return <StatisticsSection selectedServerId={selectedServerId} />;
      case 'servers':
        return <ServersSection />;
      case 'invite-create':
        return <InviteCreateSection selectedServerId={selectedServerId} />;
      case 'invite-check':
        return <InviteCheckSection selectedServerId={selectedServerId} />;
      case 'invite-list':
        return <InviteListSection user={user} selectedServerId={selectedServerId} />;
      case 'players':
        return <PlayersSection user={user} selectedServerId={selectedServerId} />;
      case 'admin-pending':
        return <AdminPendingSection selectedServerId={selectedServerId} onPendingCountChange={setPendingIpRequests} />;
      case 'broadcast':
        return <BroadcastSection user={user} selectedServerId={selectedServerId} />;
      case 'command-queue':
        return <CommandQueueSection user={user} selectedServerId={selectedServerId} />;
      case 'audit':
        return <AuditSection selectedServerId={selectedServerId} />;
      case 'errors':
        return <ErrorsSection />;
      default:
        return null;
    }
  };

  const visibleSections = sections.filter((section) => section.visible);
  const activeSectionMeta = sections.find((section) => section.id === activeSection);

  const selectSection = (sectionId: SectionId) => {
    setActiveSection(sectionId);
    storeSelectedSection(user, sectionId);
  };

  const renderSectionTabs = (className: string) => (
    <nav className={className}>
      {visibleSections.map((section) => {
        const counterValue =
          section.id === 'notifications'
            ? unreadNotifications
            : section.id === 'pending-auth'
              ? pendingAuthRequests
              : section.id === 'admin-pending'
                ? pendingIpRequests
                : 0;
        const counterClass =
          section.id === 'pending-auth'
            ? 'nav-item-counter-success'
            : section.id === 'admin-pending'
              ? 'nav-item-counter-warning'
              : 'nav-item-counter-danger';

        return (
          <button
            key={section.id}
            className={cx('nav-item', activeSection === section.id && 'nav-item-active')}
            type="button"
            onClick={() => selectSection(section.id)}
          >
            <span className="nav-item-icon" aria-hidden="true">
              <SvgIcon name={section.icon} className={cx('nav-svg-icon', `nav-svg-icon-${section.id}`)} />
            </span>
            <span className="nav-item-label">{section.label}</span>
            {counterValue > 0 ? (
              <span className={cx('nav-item-counter', counterClass)} aria-label={`${section.label}: ${counterValue}`}>
                {counterValue > 99 ? '99+' : counterValue}
              </span>
            ) : null}
          </button>
        );
      })}
    </nav>
  );

  return (
    <div className="dashboard-shell">
      <aside className="sidebar">
        <div className="sidebar-brand">
          <div className="brand-mark" aria-hidden="true">
            <img className="brand-mark-icon" src="/favicon.svg" alt="" />
          </div>
          <div>
            <div className="brand-title">{session.app.name}</div>
            <div className="brand-subtitle">
              {locale === 'ru'
                ? '\u0412\u043d\u0443\u0442\u0440\u0438\u0438\u0433\u0440\u043e\u0432\u0430\u044f \u0432\u0435\u0440\u0438\u0444\u0438\u043a\u0430\u0446\u0438\u044f \u0434\u043b\u044f Minecraft \u0441\u0435\u0440\u0432\u0435\u0440\u043e\u0432.'
                : 'In-game verification for Minecraft servers.'}
            </div>
          </div>
        </div>

        <div className="sidebar-user">
          <div className="user-name">{t('dashboardGreeting', { name: user.displayName ?? 'Player' })}</div>
          <div className="user-meta">
            {user.nickname ?? '—'} · #{user.inviteCodeId}
          </div>
        </div>

        {renderSectionTabs('nav-list nav-list-sidebar')}
      </aside>

      <main className="dashboard-main">
        <header className="topbar">
          <div className="topbar-summary">
            <h1 className="topbar-title">
              {activeSectionMeta ? (
                <SvgIcon
                  name={activeSectionMeta.icon}
                  className={cx('topbar-title-icon', `nav-svg-icon-${activeSectionMeta.id}`)}
                />
              ) : null}
              <span>{activeSectionMeta?.label ?? t('overview')}</span>
            </h1>
            <p>{sectionDescription(activeSection, t)}</p>
          </div>

          <div className="topbar-center">
            <label className="field-inline topbar-server-field">
              <select
                className="select-input"
                value={selectedServerId ?? ''}
                onChange={(event) => setSelectedServerId(event.target.value ? Number(event.target.value) : null)}
                disabled={!user.isGlobalMaster && user.serverId != null}
              >
                {user.isGlobalMaster ? <option value="">{t('allServers')}</option> : null}
                {user.accessibleServers.map((server) => (
                  <option key={server.id} value={server.id}>
                    #{server.id} · {server.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="topbar-actions">
            <TelegramLinkButton />

            <div className="locale-switcher compact-switcher">
              <select
                className="select-input locale-select"
                value={locale}
                onChange={(event) => setLocale(event.target.value as Locale)}
                aria-label={t('language')}
              >
                <option value="ru">{localeLabel('ru', t)}</option>
                <option value="en">{localeLabel('en', t)}</option>
              </select>
            </div>

            <button className="danger-button" type="button" onClick={handleLogout} disabled={logoutBusy}>
              {logoutBusy ? <ButtonLoading label={t('loading')} /> : t('logout')}
            </button>
          </div>
        </header>

        {renderSectionTabs('nav-list nav-list-mobile')}

        {renderSection()}
      </main>
    </div>
  );
}

function SectionCard({
  title,
  icon,
  actions,
  children,
  className
}: {
  title: string;
  icon?: IconName;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx('section-card', className)}>
      <div className="section-card-header">
        <h2 className="section-card-title">
          {icon ? <SvgIcon name={icon} className={cx('section-card-title-icon', `section-card-title-icon-${icon}`)} /> : null}
          <span>{title}</span>
        </h2>
        {actions ? <div className="section-card-actions">{actions}</div> : null}
      </div>
      {children}
    </section>
  );
}

function NotificationAttachments({
  attachments,
  locale,
  deletingAttachmentId,
  onDelete
}: {
  attachments: NotificationAttachmentItem[];
  locale: Locale;
  deletingAttachmentId?: number | null;
  onDelete?: (attachment: NotificationAttachmentItem) => void;
}) {
  const { t } = useI18n();
  if (attachments.length === 0) {
    return null;
  }

  return (
    <div className="notification-attachments" aria-label={locale === 'ru' ? 'Вложения' : 'Attachments'}>
      {attachments.map((attachment) => (
        <div key={attachment.id} className="notification-attachment-row">
          <a className="notification-attachment-link" href={attachment.downloadUrl}>
            <SvgIcon name="paperclip" className="notification-attachment-icon" />
            <span className="notification-attachment-name" title={attachment.fileName}>
              {attachment.fileName}
            </span>
            <span className="notification-attachment-size">{formatFileSize(locale, attachment.fileSize)}</span>
          </a>
          {onDelete ? (
            <button
              className="danger-button table-action-button notification-attachment-delete"
              type="button"
              onClick={() => onDelete(attachment)}
              disabled={deletingAttachmentId != null}
              aria-label={t('delete')}
              title={t('delete')}
            >
              {deletingAttachmentId === attachment.id ? (
                <ButtonLoading label={t('loading')} />
              ) : (
                <SvgIcon name="trash" className="button-svg-icon" />
              )}
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function RefreshButton({ onClick, label }: { onClick: () => void | Promise<void>; label: string }) {
  const [refreshing, setRefreshing] = useState(false);

  const handleClick = async () => {
    if (refreshing) {
      return;
    }

    setRefreshing(true);
    try {
      await onClick();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <button className="ghost-button refresh-button" type="button" onClick={handleClick} disabled={refreshing} aria-label={label} title={label}>
      {refreshing ? <ButtonLoading label={label} /> : <SvgIcon name="refresh" className="button-svg-icon" />}
    </button>
  );
}

function DateTimePresetField({
  label,
  value,
  onChange,
  presets,
  locale,
  emptyLabel
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  presets: DatePresetOption[];
  locale: Locale;
  emptyLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const calendarLocale = locale === 'ru' ? 'ru-RU' : 'en-US';
  const activePreset = useMemo(() => presets.find((preset) => preset.value === value), [presets, value]);
  const selectedDate = useMemo(() => (activePreset ? null : parseDateTimeLocalValue(value)), [activePreset, value]);
  const [viewDate, setViewDate] = useState<Date>(() => selectedDate ?? new Date());

  useEffect(() => {
    if (!open) {
      return;
    }
    setViewDate(selectedDate ?? new Date());
  }, [open, selectedDate]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: Event) => {
      const target = event.target as Node | null;
      if (rootRef.current && target && !rootRef.current.contains(target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const weekdayLabels = useMemo(() => calendarWeekdayLabels(locale), [locale]);
  const calendarDays = useMemo(() => buildCalendarDays(viewDate, locale), [locale, viewDate]);

  const monthTitle = useMemo(
    () =>
      new Intl.DateTimeFormat(calendarLocale, {
        month: 'long',
        year: 'numeric'
      }).format(viewDate),
    [calendarLocale, viewDate]
  );

  const dayAriaFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(calendarLocale, {
        dateStyle: 'full'
      }),
    [calendarLocale]
  );

  const hourOptions = useMemo(() => Array.from({ length: 24 }, (_, index) => index), []);
  const minuteOptions = useMemo(() => Array.from({ length: 60 }, (_, index) => index), []);
  const timePresets = useMemo(
    () => [
      { id: 'midnight', label: '00:00', hour: 0, minute: 0 },
      { id: 'morning', label: '09:00', hour: 9, minute: 0 },
      { id: 'noon', label: '12:00', hour: 12, minute: 0 },
      { id: 'evening', label: '18:00', hour: 18, minute: 0 },
      { id: 'late', label: '23:59', hour: 23, minute: 59 }
    ],
    []
  );

  const previewLabel = activePreset?.label ?? (selectedDate ? formatDate(locale, value) : emptyLabel);
  const previewDate = selectedDate ?? new Date();
  const selectedHour = previewDate.getHours();
  const selectedMinute = previewDate.getMinutes();

  const calendarButtonLabel = locale === 'ru' ? 'Открыть календарь' : 'Open calendar';
  const selectedLabel = locale === 'ru' ? 'Выбрано' : 'Selected';
  const timeLabel = locale === 'ru' ? 'Время' : 'Time';
  const presetsLabel = locale === 'ru' ? 'Быстрые варианты' : 'Quick picks';
  const hoursLabel = locale === 'ru' ? 'Часы' : 'Hours';
  const minutesLabel = locale === 'ru' ? 'Минуты' : 'Minutes';
  const clearLabel = locale === 'ru' ? 'Очистить' : 'Clear';
  const todayLabel = locale === 'ru' ? 'Сегодня' : 'Today';
  const doneLabel = locale === 'ru' ? 'Готово' : 'Done';
  const previousMonthLabel = locale === 'ru' ? 'Предыдущий месяц' : 'Previous month';
  const nextMonthLabel = locale === 'ru' ? 'Следующий месяц' : 'Next month';
  const currentMonthLabel = locale === 'ru' ? 'Перейти к текущему месяцу' : 'Jump to current month';

  const commitValue = (next: Date) => {
    onChange(toDateTimeLocal(next));
  };

  const applyDay = (day: Date) => {
    const seed = selectedDate ?? new Date();
    const next = new Date(day.getFullYear(), day.getMonth(), day.getDate(), seed.getHours(), seed.getMinutes(), 0, 0);
    commitValue(next);
    setViewDate(next);
  };

  const applyHour = (hour: number) => {
    const seed = selectedDate ?? new Date();
    commitValue(new Date(seed.getFullYear(), seed.getMonth(), seed.getDate(), hour, seed.getMinutes(), 0, 0));
  };

  const applyMinute = (minute: number) => {
    const seed = selectedDate ?? new Date();
    commitValue(new Date(seed.getFullYear(), seed.getMonth(), seed.getDate(), seed.getHours(), minute, 0, 0));
  };

  const applyTimePreset = (hour: number, minute: number) => {
    const seed = selectedDate ?? new Date();
    commitValue(new Date(seed.getFullYear(), seed.getMonth(), seed.getDate(), hour, minute, 0, 0));
  };

  const selectToday = () => {
    const now = new Date();
    const seed = selectedDate ?? now;
    const next = new Date(now.getFullYear(), now.getMonth(), now.getDate(), seed.getHours(), seed.getMinutes(), 0, 0);
    commitValue(next);
    setViewDate(next);
  };

  const clearValue = () => {
    onChange('');
    setViewDate(new Date());
  };

  return (
    <div className="field-stack datetime-field">
      <span>{label}</span>

      <div className="datetime-popover-wrap" ref={rootRef}>
        <div className={cx('datetime-trigger-shell', open && 'datetime-trigger-shell-open')}>
          <button
            className="datetime-trigger-main"
            type="button"
            onClick={() => setOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={open}
            title={previewLabel}
          >
            <span className={cx('datetime-trigger-value', !value && 'datetime-trigger-value-empty')}>{previewLabel}</span>
          </button>

          <button
            className={cx('ghost-button', 'datetime-emoji-button', open && 'ghost-button-active')}
            type="button"
            onClick={() => setOpen((current) => !current)}
            aria-label={calendarButtonLabel}
            title={calendarButtonLabel}
            aria-haspopup="dialog"
            aria-expanded={open}
          >
            <SvgIcon name="calendar" className="button-svg-icon" />
          </button>
        </div>

        {open ? (
          <div className="datetime-popover" role="dialog" aria-label={label}>
            <div className="datetime-selected-preview">
              <SvgIcon name="calendarCheck" className="datetime-selected-preview-icon" />

              <div className="datetime-selected-preview-body">
                <span className="datetime-selected-preview-label">{selectedLabel}</span>
                <span className={cx('datetime-selected-preview-value', !value && 'datetime-selected-preview-value-empty')}>
                  {previewLabel}
                </span>
              </div>
            </div>

            <div className="datetime-picker-layout">
              <div className="datetime-calendar-panel">
                <div className="datetime-calendar-header">
                  <button
                    className="ghost-button datetime-calendar-nav"
                    type="button"
                    onClick={() => setViewDate((current) => shiftCalendarMonth(current, -1))}
                    aria-label={previousMonthLabel}
                    title={previousMonthLabel}
                  >
                    <SvgIcon name="chevronLeft" className="button-svg-icon" />
                  </button>

                  <button
                    className="ghost-button datetime-calendar-month"
                    type="button"
                    onClick={() => setViewDate(new Date())}
                    aria-label={currentMonthLabel}
                    title={currentMonthLabel}
                  >
                    {monthTitle}
                  </button>

                  <button
                    className="ghost-button datetime-calendar-nav"
                    type="button"
                    onClick={() => setViewDate((current) => shiftCalendarMonth(current, 1))}
                    aria-label={nextMonthLabel}
                    title={nextMonthLabel}
                  >
                    <SvgIcon name="chevronRight" className="button-svg-icon" />
                  </button>
                </div>

                <div className="datetime-weekdays">
                  {weekdayLabels.map((dayName, index) => (
                    <span key={`${dayName}-${index}`} className="datetime-weekday">
                      {dayName}
                    </span>
                  ))}
                </div>

                <div className="datetime-days-grid">
                  {calendarDays.map((day) => {
                    const isOutsideMonth = day.getMonth() !== viewDate.getMonth();
                    const isToday = isSameCalendarDay(day, new Date());
                    const isSelected = isSameCalendarDay(day, selectedDate);

                    return (
                      <button
                        key={`${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`}
                        className={cx(
                          'datetime-day-button',
                          isOutsideMonth && 'datetime-day-button-outside',
                          isToday && 'datetime-day-button-today',
                          isSelected && 'datetime-day-button-active'
                        )}
                        type="button"
                        onClick={() => applyDay(day)}
                        aria-label={dayAriaFormatter.format(day)}
                        aria-pressed={isSelected}
                      >
                        {day.getDate()}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="datetime-side-panel">
                <div className="datetime-time-panel">
                  <div className="datetime-panel-title">{timeLabel}</div>

                  <div className="datetime-time-selects">
                    <label className="field-stack">
                      <span>{hoursLabel}</span>
                      <select
                        className="select-input"
                        value={selectedHour}
                        onChange={(event) => applyHour(Number(event.target.value))}
                      >
                        {hourOptions.map((hour) => (
                          <option key={hour} value={hour}>
                            {pad2(hour)}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="field-stack">
                      <span>{minutesLabel}</span>
                      <select
                        className="select-input"
                        value={selectedMinute}
                        onChange={(event) => applyMinute(Number(event.target.value))}
                      >
                        {minuteOptions.map((minute) => (
                          <option key={minute} value={minute}>
                            {pad2(minute)}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <div className="datetime-time-chips">
                    {timePresets.map((preset) => {
                      const active = !activePreset && selectedHour === preset.hour && selectedMinute === preset.minute;
                      return (
                        <button
                          key={preset.id}
                          className={cx('ghost-button', 'datetime-time-chip', active && 'ghost-button-active')}
                          type="button"
                          onClick={() => applyTimePreset(preset.hour, preset.minute)}
                          aria-pressed={active}
                        >
                          {preset.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {presets.length > 0 ? (
                  <div className="datetime-presets-panel">
                    <div className="datetime-panel-title">{presetsLabel}</div>

                    <div className="datetime-presets">
                      {presets.map((preset) => (
                        <button
                          key={preset.id}
                          className={cx(
                            'ghost-button',
                            'datetime-preset-button',
                            value === preset.value && 'datetime-preset-button-active'
                          )}
                          type="button"
                          onClick={() => {
                            onChange(preset.value);
                            setOpen(false);
                          }}
                          aria-pressed={value === preset.value}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="datetime-popover-footer">
              <button className="ghost-button datetime-footer-secondary" type="button" onClick={clearValue}>
                {clearLabel}
              </button>

              <button className="ghost-button datetime-footer-secondary" type="button" onClick={selectToday}>
                {todayLabel}
              </button>

              <button className="primary-button datetime-footer-button" type="button" onClick={() => setOpen(false)}>
                {doneLabel}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Alert({ info }: { info: ApiInfo | null }) {
  const [visibleInfo, setVisibleInfo] = useState<ApiInfo | null>(null);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (!info) {
      setVisibleInfo(null);
      setClosing(false);
      return;
    }

    setVisibleInfo(info);
    setClosing(false);

    const hideTimer = window.setTimeout(() => {
      setClosing(true);
    }, 5_000);

    return () => window.clearTimeout(hideTimer);
  }, [info]);

  useEffect(() => {
    if (!closing) {
      return;
    }

    const removeTimer = window.setTimeout(() => {
      setVisibleInfo(null);
    }, 240);

    return () => window.clearTimeout(removeTimer);
  }, [closing]);

  if (!visibleInfo) {
    return null;
  }
  return (
    <div
      className={cx(
        'alert',
        visibleInfo.tone === 'success' ? 'alert-success' : 'alert-error',
        'alert-floating',
        closing && 'alert-fade-out'
      )}
    >
      <SvgIcon
        name={visibleInfo.tone === 'success' ? 'checkCircle' : 'alertTriangle'}
        className="alert-icon"
      />
      {visibleInfo.message}
    </div>
  );
}

function EmptyState() {
  const { t } = useI18n();
  return (
    <div className="empty-state">
      <SvgIcon name="empty" className="empty-state-icon" />
      <span>{t('emptyState')}</span>
    </div>
  );
}

function PermissionCheckbox({
  locale,
  permission,
  checked,
  helpOpen,
  onToggle,
  onSetHelpOpen
}: {
  locale: Locale;
  permission: string;
  checked: boolean;
  helpOpen: boolean;
  onToggle: () => void;
  onSetHelpOpen: (next: boolean) => void;
}) {
  const inputId = useId();
  const tooltipId = useId();
  const label = permissionLabel(locale, permission);
  const description = permissionDescription(locale, permission);
  const helpLabel = locale === 'ru' ? `Описание права: ${label}` : `Permission description: ${label}`;

  return (
    <div className="permission-box">
      <label htmlFor={inputId} className="checkbox-row permission-box-main">
        <input id={inputId} type="checkbox" checked={checked} onChange={onToggle} />
        <span className="permission-box-label">{label}</span>
      </label>

      <div className="permission-help" data-open={helpOpen ? 'true' : 'false'}>
        <button
          className="ghost-button permission-help-button"
          type="button"
          title={description}
          aria-label={helpLabel}
          aria-describedby={tooltipId}
          aria-expanded={helpOpen}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onSetHelpOpen(!helpOpen);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              onSetHelpOpen(false);
            }
          }}
        >
          i
        </button>

        <div id={tooltipId} role="tooltip" className="permission-help-tooltip">
          {description}
        </div>
      </div>
    </div>
  );
}

function LoadingInline() {
  const { t } = useI18n();
  return (
    <div className="loading-inline" role="status" aria-label={t('loadingSection')}>
      <span className="loading-dot" />
      <span className="loading-dot" />
      <span className="loading-dot" />
    </div>
  );
}

function ButtonLoading({ label }: { label: string }) {
  return (
    <span className="button-loading" role="status" aria-label={label}>
      <span className="loading-dot" />
      <span className="loading-dot" />
      <span className="loading-dot" />
    </span>
  );
}

function PaginationControls({
  page,
  hasNext,
  loading,
  onPageChange
}: {
  page: number;
  hasNext: boolean;
  loading: boolean;
  onPageChange: (page: number) => void;
}) {
  const { t } = useI18n();

  if (page <= 1 && !hasNext) {
    return null;
  }

  return (
    <div className="pagination-row" role="navigation" aria-label={t('pagination')}>
      <button
        className="ghost-button pagination-button pagination-arrow"
        type="button"
        aria-label={t('previousPage')}
        disabled={loading || page <= 1}
        onClick={() => onPageChange(Math.max(1, page - 1))}
      >
        <SvgIcon name="chevronLeft" className="button-svg-icon" />
      </button>
      <span className="pagination-current" aria-label={t('pageNumber', { page })}>{page}</span>
      <button
        className="ghost-button pagination-button pagination-arrow"
        type="button"
        aria-label={t('nextPage')}
        disabled={loading || !hasNext}
        onClick={() => onPageChange(page + 1)}
      >
        <SvgIcon name="chevronRight" className="button-svg-icon" />
      </button>
    </div>
  );
}

const SKIN_FACE_ORDER: SkinFaceName[] = ['back', 'right', 'left', 'top', 'bottom', 'front'];

const SKIN_CUBOIDS: SkinCuboid[] = [
  {
    className: 'skin-head',
    left: 4,
    top: 0,
    width: 8,
    height: 8,
    depth: 8,
    faces: {
      top: { x: 8, y: 0, w: 8, h: 8 },
      bottom: { x: 16, y: 0, w: 8, h: 8 },
      right: { x: 0, y: 8, w: 8, h: 8 },
      front: { x: 8, y: 8, w: 8, h: 8 },
      left: { x: 16, y: 8, w: 8, h: 8 },
      back: { x: 24, y: 8, w: 8, h: 8 }
    }
  },
  {
    className: 'skin-body',
    left: 4,
    top: 8,
    width: 8,
    height: 12,
    depth: 4,
    faces: {
      top: { x: 20, y: 16, w: 8, h: 4 },
      bottom: { x: 28, y: 16, w: 8, h: 4 },
      right: { x: 16, y: 20, w: 4, h: 12 },
      front: { x: 20, y: 20, w: 8, h: 12 },
      left: { x: 28, y: 20, w: 4, h: 12 },
      back: { x: 32, y: 20, w: 8, h: 12 }
    }
  },
  {
    className: 'skin-arm skin-arm-right',
    left: 0,
    top: 8,
    width: 4,
    height: 12,
    depth: 4,
    slimLeft: 1,
    slimWidth: 3,
    faces: {
      top: { x: 44, y: 16, w: 4, h: 4 },
      bottom: { x: 48, y: 16, w: 4, h: 4 },
      right: { x: 40, y: 20, w: 4, h: 12 },
      front: { x: 44, y: 20, w: 4, h: 12 },
      left: { x: 48, y: 20, w: 4, h: 12 },
      back: { x: 52, y: 20, w: 4, h: 12 }
    },
    slimFaces: {
      top: { x: 44, y: 16, w: 3, h: 4 },
      bottom: { x: 47, y: 16, w: 3, h: 4 },
      right: { x: 40, y: 20, w: 4, h: 12 },
      front: { x: 44, y: 20, w: 3, h: 12 },
      left: { x: 47, y: 20, w: 4, h: 12 },
      back: { x: 51, y: 20, w: 3, h: 12 }
    }
  },
  {
    className: 'skin-arm skin-arm-left',
    left: 12,
    top: 8,
    width: 4,
    height: 12,
    depth: 4,
    slimWidth: 3,
    faces: {
      top: { x: 36, y: 48, w: 4, h: 4 },
      bottom: { x: 40, y: 48, w: 4, h: 4 },
      right: { x: 32, y: 52, w: 4, h: 12 },
      front: { x: 36, y: 52, w: 4, h: 12 },
      left: { x: 40, y: 52, w: 4, h: 12 },
      back: { x: 44, y: 52, w: 4, h: 12 }
    },
    slimFaces: {
      top: { x: 36, y: 48, w: 3, h: 4 },
      bottom: { x: 39, y: 48, w: 3, h: 4 },
      right: { x: 32, y: 52, w: 4, h: 12 },
      front: { x: 36, y: 52, w: 3, h: 12 },
      left: { x: 39, y: 52, w: 4, h: 12 },
      back: { x: 43, y: 52, w: 3, h: 12 }
    },
    legacyFaces: {
      top: { x: 44, y: 16, w: 4, h: 4 },
      bottom: { x: 48, y: 16, w: 4, h: 4 },
      right: { x: 40, y: 20, w: 4, h: 12 },
      front: { x: 44, y: 20, w: 4, h: 12 },
      left: { x: 48, y: 20, w: 4, h: 12 },
      back: { x: 52, y: 20, w: 4, h: 12 }
    }
  },
  {
    className: 'skin-leg skin-leg-right',
    left: 4,
    top: 20,
    width: 4,
    height: 12,
    depth: 4,
    faces: {
      top: { x: 4, y: 16, w: 4, h: 4 },
      bottom: { x: 8, y: 16, w: 4, h: 4 },
      right: { x: 0, y: 20, w: 4, h: 12 },
      front: { x: 4, y: 20, w: 4, h: 12 },
      left: { x: 8, y: 20, w: 4, h: 12 },
      back: { x: 12, y: 20, w: 4, h: 12 }
    }
  },
  {
    className: 'skin-leg skin-leg-left',
    left: 8,
    top: 20,
    width: 4,
    height: 12,
    depth: 4,
    faces: {
      top: { x: 20, y: 48, w: 4, h: 4 },
      bottom: { x: 24, y: 48, w: 4, h: 4 },
      right: { x: 16, y: 52, w: 4, h: 12 },
      front: { x: 20, y: 52, w: 4, h: 12 },
      left: { x: 24, y: 52, w: 4, h: 12 },
      back: { x: 28, y: 52, w: 4, h: 12 }
    },
    legacyFaces: {
      top: { x: 4, y: 16, w: 4, h: 4 },
      bottom: { x: 8, y: 16, w: 4, h: 4 },
      right: { x: 0, y: 20, w: 4, h: 12 },
      front: { x: 4, y: 20, w: 4, h: 12 },
      left: { x: 8, y: 20, w: 4, h: 12 },
      back: { x: 12, y: 20, w: 4, h: 12 }
    }
  },
  {
    className: 'skin-head skin-layer',
    left: 4,
    top: 0,
    width: 8,
    height: 8,
    depth: 8,
    layer: true,
    legacyHeadLayer: true,
    faces: {
      top: { x: 40, y: 0, w: 8, h: 8 },
      bottom: { x: 48, y: 0, w: 8, h: 8 },
      right: { x: 32, y: 8, w: 8, h: 8 },
      front: { x: 40, y: 8, w: 8, h: 8 },
      left: { x: 48, y: 8, w: 8, h: 8 },
      back: { x: 56, y: 8, w: 8, h: 8 }
    }
  },
  {
    className: 'skin-body skin-layer',
    left: 4,
    top: 8,
    width: 8,
    height: 12,
    depth: 4,
    layer: true,
    modernOnly: true,
    faces: {
      top: { x: 20, y: 32, w: 8, h: 4 },
      bottom: { x: 28, y: 32, w: 8, h: 4 },
      right: { x: 16, y: 36, w: 4, h: 12 },
      front: { x: 20, y: 36, w: 8, h: 12 },
      left: { x: 28, y: 36, w: 4, h: 12 },
      back: { x: 32, y: 36, w: 8, h: 12 }
    }
  },
  {
    className: 'skin-arm skin-arm-right skin-layer',
    left: 0,
    top: 8,
    width: 4,
    height: 12,
    depth: 4,
    layer: true,
    modernOnly: true,
    slimLeft: 1,
    slimWidth: 3,
    faces: {
      top: { x: 44, y: 32, w: 4, h: 4 },
      bottom: { x: 48, y: 32, w: 4, h: 4 },
      right: { x: 40, y: 36, w: 4, h: 12 },
      front: { x: 44, y: 36, w: 4, h: 12 },
      left: { x: 48, y: 36, w: 4, h: 12 },
      back: { x: 52, y: 36, w: 4, h: 12 }
    },
    slimFaces: {
      top: { x: 44, y: 32, w: 3, h: 4 },
      bottom: { x: 47, y: 32, w: 3, h: 4 },
      right: { x: 40, y: 36, w: 4, h: 12 },
      front: { x: 44, y: 36, w: 3, h: 12 },
      left: { x: 47, y: 36, w: 4, h: 12 },
      back: { x: 51, y: 36, w: 3, h: 12 }
    }
  },
  {
    className: 'skin-arm skin-arm-left skin-layer',
    left: 12,
    top: 8,
    width: 4,
    height: 12,
    depth: 4,
    layer: true,
    modernOnly: true,
    slimWidth: 3,
    faces: {
      top: { x: 52, y: 48, w: 4, h: 4 },
      bottom: { x: 56, y: 48, w: 4, h: 4 },
      right: { x: 48, y: 52, w: 4, h: 12 },
      front: { x: 52, y: 52, w: 4, h: 12 },
      left: { x: 56, y: 52, w: 4, h: 12 },
      back: { x: 60, y: 52, w: 4, h: 12 }
    },
    slimFaces: {
      top: { x: 52, y: 48, w: 3, h: 4 },
      bottom: { x: 55, y: 48, w: 3, h: 4 },
      right: { x: 48, y: 52, w: 4, h: 12 },
      front: { x: 52, y: 52, w: 3, h: 12 },
      left: { x: 55, y: 52, w: 4, h: 12 },
      back: { x: 59, y: 52, w: 3, h: 12 }
    }
  },
  {
    className: 'skin-leg skin-leg-right skin-layer',
    left: 4,
    top: 20,
    width: 4,
    height: 12,
    depth: 4,
    layer: true,
    modernOnly: true,
    faces: {
      top: { x: 4, y: 32, w: 4, h: 4 },
      bottom: { x: 8, y: 32, w: 4, h: 4 },
      right: { x: 0, y: 36, w: 4, h: 12 },
      front: { x: 4, y: 36, w: 4, h: 12 },
      left: { x: 8, y: 36, w: 4, h: 12 },
      back: { x: 12, y: 36, w: 4, h: 12 }
    }
  },
  {
    className: 'skin-leg skin-leg-left skin-layer',
    left: 8,
    top: 20,
    width: 4,
    height: 12,
    depth: 4,
    layer: true,
    modernOnly: true,
    faces: {
      top: { x: 4, y: 48, w: 4, h: 4 },
      bottom: { x: 8, y: 48, w: 4, h: 4 },
      right: { x: 0, y: 52, w: 4, h: 12 },
      front: { x: 4, y: 52, w: 4, h: 12 },
      left: { x: 8, y: 52, w: 4, h: 12 },
      back: { x: 12, y: 52, w: 4, h: 12 }
    }
  }
];

function SkinModel({
  textureUrl,
  variant,
  side,
  label,
  atlasHeight,
  legacyHeadLayerVisible
}: {
  textureUrl: string;
  variant: SkinVariant | null;
  side: 'front' | 'back';
  label: string;
  atlasHeight: SkinAtlasHeight;
  legacyHeadLayerVisible: boolean;
}) {
  const scale = 5;
  const legacy = atlasHeight === 32;
  const slim = !legacy && variant === 'SLIM';
  return (
    <div className="skin-model-wrap">
      <div className="skin-scene" aria-label={label}>
        <div className={cx('skin-model', side === 'back' && 'skin-model-back')}>
          {SKIN_CUBOIDS.map((part) => {
            if (legacy && part.modernOnly) {
              return null;
            }
            if (legacy && part.legacyHeadLayer && !legacyHeadLayerVisible) {
              return null;
            }

            const skinWidth = slim && part.slimWidth ? part.slimWidth : part.width;
            const skinLeft = slim && part.slimLeft != null ? part.slimLeft : part.left;
            const layerGrow = part.layer ? 2 : 0;
            const partWidth = skinWidth * scale + layerGrow;
            const partHeight = part.height * scale + layerGrow;
            const partDepth = part.depth * scale + layerGrow;

            return (
              <div
                key={`${side}-${part.className}`}
                className={cx('skin-cuboid', part.className)}
                style={{
                  '--skin-x': `${skinLeft * scale - layerGrow / 2}px`,
                  '--skin-y': `${part.top * scale - layerGrow / 2}px`,
                  '--skin-w': `${partWidth}px`,
                  '--skin-h': `${partHeight}px`,
                  '--skin-d': `${partDepth}px`
                } as CssVars}
              >
                {SKIN_FACE_ORDER.map((faceName) => {
                  const faces = legacy && part.legacyFaces ? part.legacyFaces : slim && part.slimFaces ? part.slimFaces : part.faces;
                  const face = faces[faceName];
                  const faceWidth = faceName === 'left' || faceName === 'right' ? partDepth : partWidth;
                  const faceHeight = faceName === 'top' || faceName === 'bottom' ? partDepth : partHeight;
                  const backgroundScaleX = 64 * (faceWidth / face.w);
                  const backgroundScaleY = atlasHeight * (faceHeight / face.h);

                  return (
                    <span
                      key={faceName}
                      className={`skin-cuboid-face skin-face-${faceName}`}
                      style={{
                        '--face-w': `${faceWidth}px`,
                        '--face-h': `${faceHeight}px`,
                        backgroundImage: `url("${textureUrl}")`,
                        backgroundSize: `${backgroundScaleX}px ${backgroundScaleY}px`,
                        backgroundPosition: `-${face.x * (faceWidth / face.w)}px -${face.y * (faceHeight / face.h)}px`
                      } as CssVars}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
      <span className="subtle-text">{label}</span>
    </div>
  );
}

function SkinPreview({
  textureUrl,
  variant,
  active
}: {
  textureUrl: string | null;
  variant: SkinVariant | null;
  active: boolean;
}) {
  const { locale } = useI18n();
  const skinTextureInfo = useSkinTextureInfo(textureUrl);
  const atlasHeight = skinAtlasHeightFromDimensions(skinTextureInfo.dimensions);
  const frontLabel = locale === 'ru' ? 'Спереди' : 'Front';
  const backLabel = locale === 'ru' ? 'Сзади' : 'Back';

  if (!textureUrl) {
    return (
      <div className={cx('skin-preview-empty', active && 'skin-preview-empty-active')}>
        <SvgIcon name="empty" />
      </div>
    );
  }

  return (
    <div className={cx('skin-preview', active && 'skin-preview-active')}>
      <SkinModel
        textureUrl={textureUrl}
        variant={variant}
        side="front"
        label={frontLabel}
        atlasHeight={atlasHeight}
        legacyHeadLayerVisible={skinTextureInfo.legacyHeadLayerVisible}
      />
      <SkinModel
        textureUrl={textureUrl}
        variant={variant}
        side="back"
        label={backLabel}
        atlasHeight={atlasHeight}
        legacyHeadLayerVisible={skinTextureInfo.legacyHeadLayerVisible}
      />
    </div>
  );
}

function playerSkinSourceLabel(locale: Locale, skin: PlayerItem['skin']): string {
  if (!skin) {
    return locale === 'ru' ? 'Скин не задан' : 'No skin set';
  }

  if (skin.sourceType === 'NICKNAME') {
    return `${locale === 'ru' ? 'Ник' : 'Name'}: ${skin.skinName ?? '—'}`;
  }

  if (skin.sourceType === 'URL') {
    return 'Texture URL';
  }

  return 'data/signature';
}

function playerSkinVariantLabel(locale: Locale, variant: SkinVariant | null): string {
  if (variant === 'SLIM') {
    return 'Slim';
  }
  if (variant === 'CLASSIC') {
    return 'Classic';
  }
  return locale === 'ru' ? 'Авто' : 'Auto';
}

function PlayerSkinThumb({ skin }: { skin: PlayerItem['skin'] }) {
  const textureUrl = skin?.skinTextureUrl ?? null;
  const skinTextureInfo = useSkinTextureInfo(textureUrl);
  const atlasHeight = skinAtlasHeightFromDimensions(skinTextureInfo.dimensions);

  if (!textureUrl) {
    return (
      <span className="player-skin-thumb player-skin-thumb-empty" aria-hidden="true">
        <SvgIcon name="userCircle" />
      </span>
    );
  }

  const textureStyle = {
    backgroundImage: `url("${textureUrl}")`,
    backgroundSize: `256px ${atlasHeight * 4}px`
  };

  return (
    <span className="player-skin-thumb" aria-hidden="true">
      <span
        className="player-skin-thumb-layer"
        style={{
          ...textureStyle,
          backgroundPosition: '-32px -32px'
        }}
      />
      {skinTextureInfo.legacyHeadLayerVisible ? (
        <span
          className="player-skin-thumb-layer"
          style={{
            ...textureStyle,
            backgroundPosition: '-160px -32px'
          }}
        />
      ) : null}
    </span>
  );
}

function PlayerSkinInfo({ skin, compact = false }: { skin: PlayerItem['skin']; compact?: boolean }) {
  const { locale, t } = useI18n();
  const statusLabel = skin?.status ? humanStatus(t, skin.status) : t('notSet');

  return (
    <div className={cx('player-skin-info', compact && 'player-skin-info-compact')}>
      <PlayerSkinThumb skin={skin} />
      <div className="player-skin-info-text">
        <div>{playerSkinSourceLabel(locale, skin)}</div>
        {skin ? (
          <div className="player-skin-meta">
            <span className={statusTextClass(skin.status)}>{statusLabel}</span>
            <span>{playerSkinVariantLabel(locale, skin.skinVariant)}</span>
          </div>
        ) : (
          <div className="subtle-text">{statusLabel}</div>
        )}
        {!compact && skin?.error ? <div className="subtle-text player-skin-error">{skin.error}</div> : null}
      </div>
    </div>
  );
}

function SkinSection({ user, selectedServerId }: { user: SessionUser; selectedServerId: number | null }) {
  const { locale, t } = useI18n();
  const [data, setData] = useState<SkinStatePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState<ApiInfo | null>(null);
  const [sourceType, setSourceType] = useState<SkinSourceType>('NICKNAME');
  const [skinName, setSkinName] = useState('');
  const [skinUrl, setSkinUrl] = useState('');
  const [skinValue, setSkinValue] = useState('');
  const [skinSignature, setSkinSignature] = useState('');
  const [skinVariant, setSkinVariant] = useState<SkinVariant | ''>('');
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [stablePreview, setStablePreview] = useState<{ textureUrl: string; variant: SkinVariant | null } | null>(null);

  const load = async (options: LoadOptions = {}) => {
    const showLoading = options.showLoading ?? true;
    if (showLoading) {
      setLoading(true);
    }
    try {
      const query = new URLSearchParams();
      if (selectedServerId) query.set('serverId', String(selectedServerId));
      const payload = await api.get<SkinStatePayload>(`/api/me/skin?${query.toString()}`);
      setData(payload);
      setInfo(keepSuccessInfo);
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    void load();
  }, [selectedServerId]);

  useEffect(() => {
    setCooldownRemaining(Math.max(0, data?.cooldownRemainingSeconds ?? 0));
  }, [data?.cooldownRemainingSeconds]);

  useEffect(() => {
    if (cooldownRemaining <= 0) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setCooldownRemaining((current) => Math.max(0, current - 1));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [cooldownRemaining]);

  const currentTextureUrl = data?.current?.skinTextureUrl ?? null;
  const currentSkinVariant = data?.current?.skinVariant ?? null;

  useEffect(() => {
    setStablePreview(null);
  }, [data?.profile?.profileId, data?.profile?.serverId]);

  useEffect(() => {
    if (!currentTextureUrl) {
      return;
    }

    setStablePreview({ textureUrl: currentTextureUrl, variant: currentSkinVariant });
  }, [currentTextureUrl, currentSkinVariant]);

  const draftTextureUrl = useMemo(() => {
    if (sourceType === 'URL') {
      return normalizeTextureUrl(skinUrl);
    }
    if (sourceType === 'PROPERTY') {
      return textureUrlFromSkinValue(skinValue);
    }
    return null;
  }, [sourceType, skinUrl, skinValue]);

  const draftSkinVariant = useMemo<SkinVariant | null>(() => {
    if (sourceType === 'URL') {
      return skinVariant || null;
    }
    if (sourceType === 'PROPERTY') {
      return skinVariant || skinVariantFromSkinValue(skinValue);
    }
    return null;
  }, [sourceType, skinVariant, skinValue]);

  const previewTextureUrl = draftTextureUrl ?? currentTextureUrl ?? stablePreview?.textureUrl ?? null;
  const previewSkinVariant = draftTextureUrl
    ? draftSkinVariant
    : currentTextureUrl
      ? currentSkinVariant
      : stablePreview?.variant ?? null;
  const latestStatus = data?.latestRequest?.status ?? data?.current?.status ?? null;
  const shouldPollSkinStatus = latestStatus === 'PENDING' || latestStatus === 'PROCESSING';
  const statusLabel = latestStatus ? humanStatus(t, latestStatus) : t('notSet');
  const currentSourceLabel =
    data?.current?.sourceType === 'NICKNAME'
      ? data.current.skinName ?? '—'
      : data?.current?.sourceType === 'URL'
        ? data.current.skinUrl ?? '—'
        : data?.current?.sourceType === 'PROPERTY'
          ? 'data/signature'
          : t('notSet');
  const sourceTitle = locale === 'ru' ? 'Источник скина' : 'Skin source';
  const skinByNameLabel = locale === 'ru' ? 'Ник скина' : 'Skin nickname';
  const skinUrlLabel = locale === 'ru' ? 'Прямая ссылка на skin texture' : 'Direct skin texture URL';
  const dataLabel = locale === 'ru' ? 'Data / value' : 'Data / value';
  const signatureLabel = locale === 'ru' ? 'Signature' : 'Signature';
  const variantLabel = locale === 'ru' ? 'Модель' : 'Model';
  const autoVariantLabel = locale === 'ru' ? 'Авто' : 'Auto';
  const guideTitle = locale === 'ru' ? 'Как загрузить свой скин через MineSkin' : 'Uploading your own skin with MineSkin';
  const submitLabel = locale === 'ru' ? 'Сменить скин' : 'Change skin';
  const queuedMessage = locale === 'ru' ? 'Заявка на смену скина поставлена в очередь.' : 'Skin change request queued.';
  const needNicknameMessage = locale === 'ru'
    ? 'Сначала привяжите никнейм в профиле.'
    : 'Bind your nickname first.';
  const needServerMessage = locale === 'ru'
    ? 'Выберите конкретный сервер перед сменой скина.'
    : 'Select a concrete server before changing skin.';
  const skinDisabledMessage = locale === 'ru'
    ? 'Смена скина отключена настройкой сервера вместе со сменой ника.'
    : 'Skin changes are disabled by the server nickname-change setting.';
  const cooldownMessage = locale === 'ru'
    ? `Подождите ${cooldownRemaining} сек. перед следующей сменой скина.`
    : `Wait ${cooldownRemaining} sec. before changing skin again.`;
  const trustedUrlHint = locale === 'ru'
    ? `Разрешены только прямые ссылки с ${TRUSTED_SKIN_TEXTURE_HOST}.`
    : `Only direct links from ${TRUSTED_SKIN_TEXTURE_HOST} are allowed.`;
  const mineskinGuide = locale === 'ru'
    ? [
        'Откройте mineskin.org и загрузите PNG-файл скина.',
        'После генерации скопируйте Value/Data и Signature в режим data/signature.',
        `Для режима URL используйте прямую ссылку textures.minecraft.net/texture/... из сгенерированного data.`
      ]
    : [
        'Open mineskin.org and upload your skin PNG.',
        'After generation, copy Value/Data and Signature into the data/signature mode.',
        `For URL mode, use the direct textures.minecraft.net/texture/... link from the generated data.`
      ];

  const canSubmit =
    !saving &&
    Boolean(data?.canRequest) &&
    !data?.requiresServerSelection &&
    !data?.skinChangeDisabled &&
    cooldownRemaining <= 0 &&
    (
      (sourceType === 'NICKNAME' && /^[A-Za-z0-9_]{3,16}$/.test(skinName.trim())) ||
      (sourceType === 'URL' && normalizeTextureUrl(skinUrl) != null) ||
      (sourceType === 'PROPERTY' && skinValue.trim().length > 0 && skinSignature.trim().length > 0 && textureUrlFromSkinValue(skinValue) != null)
    );

  const requestBlockedMessage = data?.requiresServerSelection
    ? needServerMessage
    : data?.skinChangeDisabled
      ? skinDisabledMessage
      : !data?.canRequest
        ? needNicknameMessage
        : cooldownRemaining > 0
          ? cooldownMessage
          : null;

  useEffect(() => {
    if (!shouldPollSkinStatus) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      void load({ showLoading: false });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [shouldPollSkinStatus, selectedServerId]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }

    setSaving(true);
    try {
      const payload = await api.post<SkinStatePayload>('/api/me/skin', {
        serverId: selectedServerId,
        sourceType,
        skinName: sourceType === 'NICKNAME' ? skinName.trim() : null,
        skinUrl: sourceType === 'URL' ? skinUrl.trim() : null,
        skinValue: sourceType === 'PROPERTY' ? skinValue.trim() : null,
        skinSignature: sourceType === 'PROPERTY' ? skinSignature.trim() : null,
        skinVariant: skinVariant || null
      });
      setData(payload);
      setInfo({ message: queuedMessage, tone: 'success' });
    } catch (error) {
      if (error instanceof ApiClientError) {
        const retryAfter = Number((error.details as { retryAfterSeconds?: number } | null)?.retryAfterSeconds ?? 0);
        if (retryAfter > 0) {
          setCooldownRemaining(Math.ceil(retryAfter));
        }
      }
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SectionCard title={t('skin')} icon="userCircle">
        <LoadingInline />
      </SectionCard>
    );
  }

  return (
    <div className="page-grid two-columns skin-page-grid">
      <SectionCard
        title={t('skin')}
        icon="userCircle"
        actions={<RefreshButton onClick={() => load({ showLoading: false })} label={t('refreshSection')} />}
      >
        <Alert info={info} />
        {requestBlockedMessage ? (
          <div className="result-box warning-box">{requestBlockedMessage}</div>
        ) : null}
        <form className="stack-md" onSubmit={handleSubmit}>
          <label className="field-stack">
            <span>{sourceTitle}</span>
            <select className="select-input" value={sourceType} onChange={(event) => setSourceType(event.target.value as SkinSourceType)}>
              <option value="NICKNAME">{skinByNameLabel}</option>
              <option value="URL">URL</option>
              <option value="PROPERTY">data/signature</option>
            </select>
          </label>

          {sourceType === 'NICKNAME' ? (
            <label className="field-stack">
              <span>{skinByNameLabel}</span>
              <input
                className="text-input"
                value={skinName}
                onChange={(event) => setSkinName(event.target.value)}
                placeholder={user.nickname ?? 'Player_123'}
                maxLength={16}
                autoComplete="off"
              />
            </label>
          ) : null}

          {sourceType === 'URL' ? (
            <label className="field-stack">
              <span>{skinUrlLabel}</span>
              <input
                className="text-input"
                value={skinUrl}
                onChange={(event) => setSkinUrl(event.target.value)}
                placeholder={`https://${TRUSTED_SKIN_TEXTURE_HOST}/texture/...`}
                autoComplete="off"
              />
              <span className="subtle-text">{trustedUrlHint}</span>
            </label>
          ) : null}

          {sourceType === 'PROPERTY' ? (
            <>
              <label className="field-stack">
                <span>{dataLabel}</span>
                <textarea className="textarea-input skin-data-textarea" value={skinValue} onChange={(event) => setSkinValue(event.target.value)} />
              </label>
              <label className="field-stack">
                <span>{signatureLabel}</span>
                <textarea className="textarea-input skin-data-textarea" value={skinSignature} onChange={(event) => setSkinSignature(event.target.value)} />
              </label>
            </>
          ) : null}

          <label className="field-stack">
            <span>{variantLabel}</span>
            <select className="select-input" value={skinVariant} onChange={(event) => setSkinVariant(event.target.value as SkinVariant | '')}>
              <option value="">{autoVariantLabel}</option>
              <option value="CLASSIC">Classic</option>
              <option value="SLIM">Slim</option>
            </select>
          </label>

          <button className="primary-button" disabled={!canSubmit}>
            {saving ? <ButtonLoading label={t('loading')} /> : submitLabel}
          </button>
        </form>
      </SectionCard>

      <div className="column-stack">
        <SectionCard title={locale === 'ru' ? 'Текущий скин' : 'Current skin'} icon="userCircle">
          <SkinPreview textureUrl={previewTextureUrl} variant={previewSkinVariant} active={saving || shouldPollSkinStatus} />
          <div className="details-grid compact-grid skin-details-grid">
            <DetailItem
              label={t('status')}
              value={<span className={statusTextClass(latestStatus)}>{statusLabel}</span>}
            />
            <DetailItem label={sourceTitle} value={currentSourceLabel} />
            <DetailItem label={variantLabel} value={data?.current?.skinVariant ?? t('notSet')} />
            <DetailItem label={t('updatedAt')} value={formatDate(locale, data?.current?.updatedAt ?? null)} />
          </div>
          {data?.latestRequest?.error ? <div className="result-box error-box">{data.latestRequest.error}</div> : null}
        </SectionCard>

        <SectionCard title={guideTitle} icon="clipboard">
          <ol className="skin-guide-list">
            {mineskinGuide.map((item, index) => {
              if (index !== 0) {
                return <li key={item}>{item}</li>;
              }

              const [beforeLink, afterLink = ''] = item.split('mineskin.org');
              return (
                <li key={item}>
                  {beforeLink}
                  <a className="skin-guide-inline-link" href="https://mineskin.org/" target="_blank" rel="noreferrer">
                    mineskin.org
                  </a>
                  {afterLink}
                </li>
              );
            })}
          </ol>
        </SectionCard>
      </div>
    </div>
  );
}

function OverviewSection({
  user,
  selectedServerId,
  onRefreshSession
}: {
  user: SessionUser;
  selectedServerId: number | null;
  onRefreshSession: () => Promise<void>;
}) {
  const { locale, t } = useI18n();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<ApiInfo | null>(null);
  const [nickname, setNickname] = useState(user.nickname ?? '');
  const [savingNickname, setSavingNickname] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('');
  const [authorizedIpsPage, setAuthorizedIpsPage] = useState(1);
  const [authorizedIpsHasNext, setAuthorizedIpsHasNext] = useState(false);
  const [deletingAuthorizedIpId, setDeletingAuthorizedIpId] = useState<number | null>(null);
  const skipNextAuthorizedIpsLoadRef = useRef(false);
  const canSaveNickname =
    nickname.trim().length >= 3 &&
    (!user.isGlobalMaster || selectedServerId != null || user.accessibleServers.length === 1);
  const nicknameUnbound = !(data?.profile?.nickname ?? user.nickname ?? '').trim();

  const load = async (authorizedIpsPageToLoad = authorizedIpsPage, options: LoadOptions = {}) => {
    const showLoading = options.showLoading ?? true;
    if (showLoading) {
      setLoading(true);
    }
    setInfo(keepSuccessInfo);
    try {
      const query = new URLSearchParams();
      if (selectedServerId) query.set('serverId', String(selectedServerId));
      appendPaginationParams(query, authorizedIpsPageToLoad, 'authorizedIpLimit', 'authorizedIpOffset');
      const payload = await api.get<DashboardData>(`/api/me/dashboard?${query.toString()}`);
      setAuthorizedIpsHasNext(hasNextPage(payload.authorizedIps ?? []));
      setData({ ...payload, authorizedIps: pageItems(payload.authorizedIps ?? []) });
      setNickname(payload.profile?.nickname ?? user.nickname ?? '');
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    setAuthorizedIpsPage(1);
    void load(1);
  }, [selectedServerId]);

  useEffect(() => {
    if (skipNextAuthorizedIpsLoadRef.current) {
      skipNextAuthorizedIpsLoadRef.current = false;
      return;
    }

    void load(authorizedIpsPage);
  }, [authorizedIpsPage]);

  const handleNicknameSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSavingNickname(true);
    setInfo(null);
    try {
      await api.post('/api/me/profile/nickname', { nickname, serverId: selectedServerId });
      await load(authorizedIpsPage, { showLoading: false });
      await onRefreshSession();
      setInfo({ message: t('formSaved'), tone: 'success' });
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      setSavingNickname(false);
    }
  };

  const handlePasswordSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (newPassword !== newPasswordConfirmation) {
      setInfo({ message: t('passwordConfirmationMismatch'), tone: 'error' });
      return;
    }

    setSavingPassword(true);
    setInfo(null);
    try {
      await api.post('/api/me/profile/password', {
        currentPassword,
        newPassword,
        newPasswordConfirmation
      });
      setCurrentPassword('');
      setNewPassword('');
      setNewPasswordConfirmation('');
      setInfo({ message: t('passwordChanged'), tone: 'success' });
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      setSavingPassword(false);
    }
  };

  const handleAuthorizedIpDelete = async (authorizedIpId: number) => {
    if (!window.confirm(t('deleteAuthorizedIpConfirm'))) {
      return;
    }

    setDeletingAuthorizedIpId(authorizedIpId);
    setInfo(null);
    try {
      await api.delete(`/api/me/authorized-ips/${authorizedIpId}`);
      const nextPage = authorizedIpsPage > 1 && (data?.authorizedIps?.length ?? 0) <= 1
        ? authorizedIpsPage - 1
        : authorizedIpsPage;

      if (nextPage !== authorizedIpsPage) {
        skipNextAuthorizedIpsLoadRef.current = true;
        setAuthorizedIpsPage(nextPage);
        await load(nextPage, { showLoading: false });
      } else {
        await load(nextPage, { showLoading: false });
      }
      setInfo({ message: t('authorizedIpDeleted'), tone: 'success' });
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      setDeletingAuthorizedIpId(null);
    }
  };

  if (loading) {
    return (
      <SectionCard title={t('overview')} icon="home">
        <LoadingInline />
      </SectionCard>
    );
  }

  return (
    <div className="page-grid two-columns">
      <div className="column-stack">
        <SectionCard
          title={t('profile')}
          icon="userCircle"
          actions={
            <RefreshButton onClick={() => load(authorizedIpsPage, { showLoading: false })} label={t('refreshSection')} />
          }
        >
          <Alert info={info} />
          {data?.profile ? (
            <div className="details-grid">
              <DetailItem label={t('server')} value={data.profile.serverName ?? t('notSet')} />
              <DetailItem label={t('nickname')} value={data.profile.nickname ?? t('notSet')} />
              <DetailItem label={t('lastSeenIp')} value={maskIpAddress(data.profile.lastSeenIp) ?? t('notSet')} />
              <DetailItem label={t('lastLoginAt')} value={formatDate(locale, data.profile.lastLoginAt)} />
              <DetailItem label={t('activatedAt')} value={formatDate(locale, data.profile.activatedAt)} />
              <DetailItem label={t('inviter')} value={data.profile.inviterName ?? t('notSet')} />
            </div>
          ) : (
            <EmptyState />
          )}
        </SectionCard>

        {hasPermission(user, 'BIND_NICKNAME') ? (
          <SectionCard title={user.nickname ? t('changeNickname') : t('bindNickname')} icon="edit">
            <form className="stack-md" onSubmit={handleNicknameSave}>
              <label className="field-stack">
                <span>{t('nickname')}</span>
                <input
                  className={cx('text-input', nicknameUnbound && 'nickname-input-intro')}
                  value={nickname}
                  onChange={(event) => setNickname(event.target.value)}
                  placeholder="Player_123"
                  maxLength={16}
                />
              </label>
              <button className="primary-button" disabled={savingNickname || !canSaveNickname}>
                {savingNickname ? <ButtonLoading label={t('loading')} /> : t('save')}
              </button>
            </form>
          </SectionCard>
        ) : null}
      </div>

      <div className="column-stack">
        <SectionCard title={t('authorizedIps')} icon="shieldCheck">
          {data && (data.authorizedIps?.length ?? 0) > 0 ? (
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('ipAddress')}</th>
                    <th>{t('source')}</th>
                    <th>{t('lastUsedAt')}</th>
                    <th>{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.authorizedIps ?? []).map((item) => (
                    <tr key={item.id}>
                      <td>
                        <code>{maskIpAddress(item.ipAddress) ?? '—'}</code>
                      </td>
                      <td>
                        <span className={badgeClass(item.source)}>{item.source}</span>
                      </td>
                      <td>{formatDate(locale, item.lastUsedAt ?? item.createdAt)}</td>
                      <td>
                        <button
                          className="danger-button table-action-button"
                          type="button"
                          disabled={deletingAuthorizedIpId === item.id}
                          onClick={() => void handleAuthorizedIpDelete(item.id)}
                        >
                          {deletingAuthorizedIpId === item.id ? <ButtonLoading label={t('loading')} /> : t('delete')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState />
          )}
          <PaginationControls
            page={authorizedIpsPage}
            hasNext={authorizedIpsHasNext}
            loading={loading}
            onPageChange={setAuthorizedIpsPage}
          />
        </SectionCard>

        {hasPermission(user, 'BIND_NICKNAME') ? (
          <SectionCard title={t('passwordChangeSection')} icon="key">
            <form className="stack-md" onSubmit={handlePasswordSave}>
              <label className="field-stack">
                <span>{t('currentPassword')}</span>
                <input
                  className="text-input"
                  type="password"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  minLength={3}
                  maxLength={16}
                  autoComplete="current-password"
                />
              </label>
              <label className="field-stack">
                <span>{t('newPassword')}</span>
                <input
                  className="text-input"
                  type="password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  minLength={3}
                  maxLength={16}
                  autoComplete="new-password"
                />
              </label>
              <label className="field-stack">
                <span>{t('confirmNewPassword')}</span>
                <input
                  className="text-input"
                  type="password"
                  value={newPasswordConfirmation}
                  onChange={(event) => setNewPasswordConfirmation(event.target.value)}
                  minLength={3}
                  maxLength={16}
                  autoComplete="new-password"
                />
              </label>
              <button
                className="primary-button"
                disabled={
                  savingPassword ||
                  currentPassword.length < 3 ||
                  newPassword.length < 3 ||
                  newPasswordConfirmation.length < 3
                }
              >
                {savingPassword ? <ButtonLoading label={t('loading')} /> : t('changePassword')}
              </button>
            </form>
          </SectionCard>
        ) : null}

      </div>
    </div>
  );
}

function StatisticsSection({ selectedServerId }: { selectedServerId: number | null }) {
  const { t } = useI18n();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<ApiInfo | null>(null);

  const load = async (options: LoadOptions = {}) => {
    const showLoading = options.showLoading ?? true;
    if (showLoading) {
      setLoading(true);
    }
    try {
      const query = new URLSearchParams();
      if (selectedServerId) {
        query.set('serverId', String(selectedServerId));
      }
      query.set('authorizedIpLimit', '1');
      query.set('includePendingAuthRequests', 'false');
      const payload = await api.get<DashboardData>(`/api/me/dashboard?${query.toString()}`);
      setData(payload);
      setInfo(keepSuccessInfo);
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    void load();
  }, [selectedServerId]);

  return (
    <SectionCard
      title={t('serverStats')}
      icon="barChart"
      actions={
        <RefreshButton onClick={() => load({ showLoading: false })} label={t('refreshSection')} />
      }
    >
      <Alert info={info} />
      {loading ? (
        <LoadingInline />
      ) : data?.stats ? (
        <div className="summary-cards">
          <MetricCard title={t('statsTotalInvites')} value={data.stats.total_invites ?? 0} />
          <MetricCard title={t('statsActivatedInvites')} value={data.stats.activated_invites ?? 0} />
          <MetricCard title={t('statsInactiveInvites')} value={data.stats.inactive_invites ?? 0} />
          <MetricCard title={t('statsRevokedInvites')} value={data.stats.revoked_invites ?? 0} />
          <MetricCard title={t('statsBoundNicknames')} value={data.stats.bound_nicknames ?? 0} />
          <MetricCard title={t('statsActiveAuthorizedIps')} value={data.stats.active_authorized_ips ?? 0} />
          <MetricCard title={t('statsPendingIpRequests')} value={data.stats.pending_ip_requests ?? 0} />
          <MetricCard title={t('statsActiveNotifications')} value={data.stats.active_notifications ?? 0} />
        </div>
      ) : (
        <EmptyState />
      )}
    </SectionCard>
  );
}

function OwnPendingAuthSection({ onPendingCountChange }: { onPendingCountChange?: (value: number) => void }) {
  const { locale, t } = useI18n();
  const [items, setItems] = useState<PendingAuthRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<ApiInfo | null>(null);
  const [quietListUpdates, setQuietListUpdates] = useState(false);
  const [decisionBusy, setDecisionBusy] = useState<string | null>(null);
  const mountedRef = useRef(false);
  const pendingLoadRef = useRef(false);

  const load = useCallback(async ({ silent = false, showLoading }: { silent?: boolean; showLoading?: boolean } = {}) => {
    if (pendingLoadRef.current) {
      return;
    }

    pendingLoadRef.current = true;
    const shouldShowLoading = showLoading ?? !silent;
    if (shouldShowLoading) {
      setQuietListUpdates(false);
      setLoading(true);
    }
    try {
      const payload = await api.get<PendingAuthRequest[]>('/api/me/pending-auth');
      if (!mountedRef.current) {
        return;
      }
      if (silent) {
        setQuietListUpdates(true);
      }
      setItems(payload);
      onPendingCountChange?.(payload.length);
      if (!silent) {
        setInfo(keepSuccessInfo);
      }
    } catch (error) {
      if (!mountedRef.current) {
        return;
      }
      onPendingCountChange?.(0);
      if (!silent) {
        setInfo({ message: (error as Error).message, tone: 'error' });
      }
    } finally {
      pendingLoadRef.current = false;
      if (shouldShowLoading && mountedRef.current) {
        setLoading(false);
      }
    }
  }, [onPendingCountChange]);

  useEffect(() => {
    mountedRef.current = true;
    void load();
    const intervalId = window.setInterval(() => void load({ silent: true }), 1_000);

    return () => {
      mountedRef.current = false;
      window.clearInterval(intervalId);
    };
  }, [load]);

  const handleDecision = async (item: PendingAuthRequest, approve: boolean) => {
    const busyKey = `${item.id}:${approve ? 'approve' : 'reject'}`;
    if (decisionBusy) {
      return;
    }

    setDecisionBusy(busyKey);
    try {
      await api.post(`/api/me/pending-auth/${item.id}/${approve ? 'approve' : 'reject'}`);
      setInfo({ message: t('formSaved'), tone: 'success' });
      setItems((current) => {
        const next = current.filter((currentItem) => currentItem.id !== item.id);
        onPendingCountChange?.(next.length);
        return next;
      });
      void load({ silent: true });
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      setDecisionBusy(null);
    }
  };

  return (
    <SectionCard
      title={t('pendingAuth')}
      icon="lock"
      actions={
        <RefreshButton onClick={() => load({ showLoading: false })} label={t('refreshSection')} />
      }
    >
      <Alert info={info} />
      {loading ? (
        <LoadingInline />
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <div className={cx('stack-md', quietListUpdates && 'quiet-refresh-list')}>
          {items.map((item) => (
            <div key={item.id} className="auth-request-card">
              <div>
                <div className="auth-question">{t('thisIsYou')}</div>
                <div className="auth-meta">
                  {item.requestedNickname ?? '—'} · {maskIpAddress(item.ipAddress) ?? '—'}
                </div>
                <div className="auth-meta">
                  {t('createdAt')}: {formatDate(locale, item.createdAt)} · {t('expiresAt')}: {formatDate(locale, item.expiresAt)}
                </div>
              </div>
              <div className="button-row">
                <button
                  className="primary-button"
                  type="button"
                  disabled={decisionBusy != null}
                  onClick={() => void handleDecision(item, true)}
                >
                  {decisionBusy === `${item.id}:approve` ? <ButtonLoading label={t('loading')} /> : t('approve')}
                </button>
                <button
                  className="danger-button"
                  type="button"
                  disabled={decisionBusy != null}
                  onClick={() => void handleDecision(item, false)}
                >
                  {decisionBusy === `${item.id}:reject` ? <ButtonLoading label={t('loading')} /> : t('reject')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

function NotificationsSection({
  selectedServerId,
  unreadCount,
  onUnreadCountChange
}: {
  selectedServerId: number | null;
  unreadCount?: number;
  onUnreadCountChange?: (value: number) => void;
}) {
  const { locale, t } = useI18n();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<ApiInfo | null>(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [readingNotificationIds, setReadingNotificationIds] = useState<Set<number>>(() => new Set<number>());
  const [markingAllRead, setMarkingAllRead] = useState(false);

  const refreshUnreadCount = async () => {
    if (!onUnreadCountChange) {
      return;
    }

    try {
      const dashboardQuery = new URLSearchParams();
      if (selectedServerId) {
        dashboardQuery.set('serverId', String(selectedServerId));
      }
      dashboardQuery.set('authorizedIpLimit', '1');
      const dashboard = await api.get<DashboardData>(`/api/me/dashboard?${dashboardQuery.toString()}`);
      onUnreadCountChange(dashboard.summary.unreadNotifications ?? 0);
    } catch {
      onUnreadCountChange(0);
    }
  };

  const load = async (pageToLoad = page, options: LoadOptions = {}) => {
    const showLoading = options.showLoading ?? true;
    if (showLoading) {
      setLoading(true);
    }
    try {
      const query = new URLSearchParams();
      if (selectedServerId) query.set('serverId', String(selectedServerId));
      appendPaginationParams(query, pageToLoad);
      const payload = await api.get<NotificationItem[]>(`/api/me/notifications?${query.toString()}`);
      setItems(pageItems(payload));
      setHasNext(hasNextPage(payload));
      void refreshUnreadCount();
      setInfo(keepSuccessInfo);
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    setPage(1);
    void load(1);
  }, [selectedServerId]);

  useEffect(() => {
    void load(page);
  }, [page]);

  const handleRead = async (notificationId: number) => {
    if (readingNotificationIds.has(notificationId)) {
      return;
    }

    setReadingNotificationIds((current) => {
      const next = new Set(current);
      next.add(notificationId);
      return next;
    });

    try {
      await api.post(`/api/me/notifications/${notificationId}/read`);
      const readAt = new Date().toISOString();
      setItems((current) =>
        current.map((item) => (item.id === notificationId ? { ...item, readAt: item.readAt ?? readAt } : item))
      );
      setInfo({ message: t('formSaved'), tone: 'success' });
      void refreshUnreadCount();
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      setReadingNotificationIds((current) => {
        const next = new Set(current);
        next.delete(notificationId);
        return next;
      });
    }
  };

  const handleReadAll = async () => {
    if (markingAllRead) {
      return;
    }

    setMarkingAllRead(true);
    try {
      await api.post<{ readCount: number }>('/api/me/notifications/read-all', { serverId: selectedServerId });
      const readAt = new Date().toISOString();
      setItems((current) => current.map((item) => (item.readAt ? item : { ...item, readAt })));
      setReadingNotificationIds(() => new Set<number>());
      setInfo({ message: t('notificationsMarkedRead'), tone: 'success' });
      void refreshUnreadCount();
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      setMarkingAllRead(false);
    }
  };

  const canMarkAllRead = unreadCount == null ? items.some((item) => !item.readAt) : unreadCount > 0;

  return (
    <SectionCard
      title={t('notifications')}
      icon="bell"
      actions={
        <>
          <button
            className="success-button notifications-read-all-button"
            type="button"
            onClick={() => void handleReadAll()}
            disabled={loading || markingAllRead || !canMarkAllRead}
          >
            {markingAllRead ? <ButtonLoading label={t('loading')} /> : t('markAllRead')}
          </button>
          <RefreshButton onClick={() => load(page, { showLoading: false })} label={t('refreshSection')} />
        </>
      }
    >
      <Alert info={info} />
      {loading ? (
        <LoadingInline />
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="stack-md notifications-list">
          {items.map((item) => (
            <div key={item.id} className="notification-card">
              <div className="notification-header-row">
                <div>
                  <h3>{item.title}</h3>
                  <div className="meta-line">
                    {item.serverName ?? t('global')} · {formatDate(locale, item.createdAt)}
                  </div>
                </div>
                <div className="badge-cluster">
                  <span className={badgeClass(item.severity)}>{item.severity}</span>
                  <span className={badgeClass(item.readAt ? 'SUCCESS' : 'PENDING')}>
                    {item.readAt ? t('notificationRead') : t('requestStatusPending')}
                  </span>
                </div>
              </div>
              <p className="notification-body">{item.body}</p>
              <NotificationAttachments attachments={item.attachments ?? []} locale={locale} />
              {!item.readAt ? (
                <button
                  className="success-button notification-read-button"
                  type="button"
                  disabled={readingNotificationIds.has(item.id)}
                  onClick={() => void handleRead(item.id)}
                >
                  {readingNotificationIds.has(item.id) ? <ButtonLoading label={t('loading')} /> : t('markRead')}
                </button>
              ) : null}
            </div>
          ))}
        </div>
      )}
      {!loading ? <PaginationControls page={page} hasNext={hasNext} loading={loading} onPageChange={setPage} /> : null}
    </SectionCard>
  );
}

function ServersSection() {
  const { locale, t } = useI18n();
  const [servers, setServers] = useState<ServerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<ApiInfo | null>(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [createForm, setCreateForm] = useState({ slug: '', name: '', description: '', disableNicknameChange: false });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({
    slug: '',
    name: '',
    description: '',
    isActive: true,
    disableNicknameChange: false
  });

  const load = async (options: LoadOptions = {}) => {
    const showLoading = options.showLoading ?? true;
    if (showLoading) {
      setLoading(true);
    }
    try {
      const payload = await api.get<ServerItem[]>('/api/admin/servers');
      setServers(payload);
      setInfo(keepSuccessInfo);
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCreating(true);
    try {
      await api.post('/api/admin/servers', createForm);
      setCreateForm({ slug: '', name: '', description: '', disableNicknameChange: false });
      setInfo({ message: t('formCreated'), tone: 'success' });
      await load({ showLoading: false });
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (server: ServerItem) => {
    setEditingId(server.id);
    setEditForm({
      slug: server.slug,
      name: server.name,
      description: server.description ?? '',
      isActive: server.isActive,
      disableNicknameChange: server.disableNicknameChange
    });
  };

  const handleUpdate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingId) {
      return;
    }
    setUpdating(true);
    try {
      await api.put(`/api/admin/servers/${editingId}`, editForm);
      setEditingId(null);
      setInfo({ message: t('formSaved'), tone: 'success' });
      await load({ showLoading: false });
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="page-grid two-columns">
      <SectionCard title={t('createServer')} icon="plusCircle">
        <Alert info={info} />
        <form className="stack-md" onSubmit={handleCreate}>
          <label className="field-stack">
            <span>{t('slug')}</span>
            <input className="text-input" value={createForm.slug} onChange={(event) => setCreateForm({ ...createForm, slug: event.target.value })} />
          </label>
          <label className="field-stack">
            <span>{t('name')}</span>
            <input className="text-input" value={createForm.name} onChange={(event) => setCreateForm({ ...createForm, name: event.target.value })} />
          </label>
          <label className="field-stack">
            <span>{t('description')}</span>
            <textarea className="textarea-input" value={createForm.description} onChange={(event) => setCreateForm({ ...createForm, description: event.target.value })} />
          </label>
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={createForm.disableNicknameChange}
              onChange={(event) => setCreateForm({ ...createForm, disableNicknameChange: event.target.checked })}
            />
            <span>{t('disableNicknameChange')}</span>
          </label>
          <button className="primary-button" disabled={creating || createForm.slug.trim().length < 2 || createForm.name.trim().length < 2}>
            {creating ? <ButtonLoading label={t('loading')} /> : t('create')}
          </button>
        </form>
      </SectionCard>

      <SectionCard
        title={t('servers')}
        icon="monitor"
        actions={
          <RefreshButton onClick={() => load({ showLoading: false })} label={t('refreshSection')} />
        }
      >
        {loading ? (
          <LoadingInline />
        ) : servers.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="stack-md">
            {servers.map((server) => (
              <div key={server.id} className="server-row">
                <div className="server-row-main">
                  <h3>
                    #{server.id} · {server.name}
                  </h3>
                  <div className="meta-line">
                    {server.slug} · {server.inviteCount} invites · {server.playerCount} players · {formatDate(locale, server.updatedAt)}
                  </div>
                  <div className="badge-cluster">
                    <span className={badgeClass(server.isActive ? 'ACTIVE' : 'INACTIVE')}>
                      {server.isActive ? t('active') : t('inactive')}
                    </span>
                    {server.disableNicknameChange ? <span className={badgeClass('WARNING')}>{t('nicknameChangeDisabled')}</span> : null}
                  </div>
                </div>
                <button className="ghost-button" type="button" onClick={() => startEdit(server)}>
                  {t('edit')}
                </button>
              </div>
            ))}
          </div>
        )}

        {editingId ? (
          <form className="stack-md section-subpanel" onSubmit={handleUpdate}>
            <h3>{t('edit')}</h3>
            <label className="field-stack">
              <span>{t('slug')}</span>
              <input className="text-input" value={editForm.slug} onChange={(event) => setEditForm({ ...editForm, slug: event.target.value })} />
            </label>
            <label className="field-stack">
              <span>{t('name')}</span>
              <input className="text-input" value={editForm.name} onChange={(event) => setEditForm({ ...editForm, name: event.target.value })} />
            </label>
            <label className="field-stack">
              <span>{t('description')}</span>
              <textarea className="textarea-input" value={editForm.description} onChange={(event) => setEditForm({ ...editForm, description: event.target.value })} />
            </label>
            <label className="checkbox-row">
              <input type="checkbox" checked={editForm.isActive} onChange={(event) => setEditForm({ ...editForm, isActive: event.target.checked })} />
              <span>{t('active')}</span>
            </label>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={editForm.disableNicknameChange}
                onChange={(event) => setEditForm({ ...editForm, disableNicknameChange: event.target.checked })}
              />
              <span>{t('disableNicknameChange')}</span>
            </label>
            <div className="button-row">
              <button className="primary-button" disabled={updating || editForm.slug.trim().length < 2 || editForm.name.trim().length < 2}>
                {updating ? <ButtonLoading label={t('loading')} /> : t('save')}
              </button>
              <button className="ghost-button" type="button" disabled={updating} onClick={() => setEditingId(null)}>
                {t('cancel')}
              </button>
            </div>
          </form>
        ) : null}
      </SectionCard>
    </div>
  );
}

function InviteCreateSection({ selectedServerId }: { selectedServerId: number | null }) {
  const { locale, t } = useI18n();
  const [info, setInfo] = useState<ApiInfo | null>(null);
  const [count, setCount] = useState(1);
  const [customCode, setCustomCode] = useState('');
  const [requiresWebPin, setRequiresWebPin] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(['BIND_NICKNAME', 'AUTHORIZE_LOGIN', 'VIEW_PROFILE']);
  const [createdCodes, setCreatedCodes] = useState<CreatedInviteCode[]>([]);
  const [creatingCodes, setCreatingCodes] = useState(false);
  const [copiedInviteCodeIds, setCopiedInviteCodeIds] = useState<number[]>([]);
  const [copiedDirectLinkInviteCodeIds, setCopiedDirectLinkInviteCodeIds] = useState<number[]>([]);
  const [openPermissionHelp, setOpenPermissionHelp] = useState<string | null>(null);
  const copyAllCodesLabel = locale === 'ru' ? 'Скопировать всё' : 'Copy all';
  const codeLoginModeLabel = locale === 'ru' ? '\u0420\u0435\u0436\u0438\u043c \u0432\u0445\u043e\u0434\u0430' : 'Login mode';
  const withPasswordLabel = locale === 'ru' ? '\u0421 \u043f\u0430\u0440\u043e\u043b\u0435\u043c' : 'With password';
  const codeOnlyLabel = locale === 'ru' ? '\u0422\u043e\u043b\u044c\u043a\u043e \u043a\u043e\u0434' : 'Code only';
  const withPasswordHelp =
    locale === 'ru'
      ? '\u041f\u0440\u0438 \u043f\u0435\u0440\u0432\u043e\u043c \u0432\u0445\u043e\u0434\u0435 \u0438\u0433\u0440\u043e\u043a \u0437\u0430\u0434\u0430\u0441\u0442 \u043f\u0430\u0440\u043e\u043b\u044c.'
      : 'The player sets a password on first sign-in.';
  const codeOnlyHelp =
    locale === 'ru'
      ? '\u0412\u0445\u043e\u0434 \u0441\u0440\u0430\u0437\u0443 \u043f\u043e \u043a\u043e\u0434\u0443 \u0438\u043b\u0438 \u043f\u043e \u0441\u0441\u044b\u043b\u043a\u0435 ?code=...'
      : 'Sign in immediately by code or by a ?code=... link.';
  const directLinkLabel = locale === 'ru' ? '\u041f\u0440\u044f\u043c\u0430\u044f \u0441\u0441\u044b\u043b\u043a\u0430' : 'Direct link';
  const copyLinkLabel = locale === 'ru' ? '\u0421\u043a\u043e\u043f\u0438\u0440\u043e\u0432\u0430\u0442\u044c \u0441\u0441\u044b\u043b\u043a\u0443' : 'Copy link';
  const codeNameLabel = locale === 'ru' ? '\u041d\u0430\u0437\u0432\u0430\u043d\u0438\u0435 \u043a\u043e\u0434\u0430' : 'Code name';
  const randomCodeNameLabel = locale === 'ru' ? '\u0421\u043b\u0443\u0447\u0430\u0439\u043d\u043e\u0435 \u043d\u0430\u0437\u0432\u0430\u043d\u0438\u0435' : 'Random name';
  const customCodeNameHelp =
    locale === 'ru'
      ? '\u041a\u0430\u0441\u0442\u043e\u043c\u043d\u044b\u0439 \u043a\u043e\u0434: 1-32 \u0441\u0438\u043c\u0432\u043e\u043b\u0430. \u041f\u0440\u0438 \u043d\u0435\u043c \u0441\u043e\u0437\u0434\u0430\u0435\u0442\u0441\u044f \u0442\u043e\u043b\u044c\u043a\u043e \u043e\u0434\u0438\u043d \u043a\u043e\u0434.'
      : 'Custom code: 1-32 characters. It creates only one code.';
  const randomCodeNameHelp =
    locale === 'ru'
      ? '\u0415\u0441\u043b\u0438 \u043e\u0441\u0442\u0430\u0432\u0438\u0442\u044c \u043f\u043e\u043b\u0435 \u043f\u0443\u0441\u0442\u044b\u043c, \u043a\u043e\u0434 \u0431\u0443\u0434\u0435\u0442 \u0441\u043b\u0443\u0447\u0430\u0439\u043d\u044b\u043c.'
      : 'Leave empty to create a random code.';
  const normalizedCustomCode = customCode.trim();
  const hasCustomCode = normalizedCustomCode.length > 0;
  const effectiveCount = hasCustomCode ? 1 : count;

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (event.target instanceof Element && event.target.closest('.permission-help')) {
        return;
      }

      setOpenPermissionHelp(null);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  const togglePermission = (permission: string) => {
    setSelectedPermissions((current) =>
      current.includes(permission) ? current.filter((value) => value !== permission) : [...current, permission]
    );
  };

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (creatingCodes) {
      return;
    }
    setCreatingCodes(true);
    try {
      const response = await api.post<{ createdCodes: CreatedInviteCode[] }>('/api/admin/invite-codes', {
        serverId: selectedServerId,
        count: effectiveCount,
        customCode: hasCustomCode ? normalizedCustomCode : null,
        requiresWebPin,
        permissions: selectedPermissions
      });
      setCreatedCodes(response.createdCodes);
      setCopiedInviteCodeIds([]);
      setCopiedDirectLinkInviteCodeIds([]);
      setInfo({ message: t('formCreated'), tone: 'success' });
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      setCreatingCodes(false);
    }
  };

  return (
    <SectionCard title={t('codeCreation')} icon="plusCircle">
      <Alert info={info} />
      <form className="stack-md" onSubmit={handleCreate}>
        <label className="field-stack">
          <span>{t('count')}</span>
          <input
            className="text-input"
            type="number"
            min={1}
            max={100}
            value={effectiveCount}
            onChange={(event) => setCount(Number(event.target.value))}
            disabled={hasCustomCode}
          />
        </label>
        <label className="field-stack">
          <span>{codeNameLabel}</span>
          <input
            className="text-input"
            value={customCode}
            onChange={(event) => setCustomCode(event.target.value.slice(0, 32))}
            placeholder={randomCodeNameLabel}
            maxLength={32}
          />
          <span className="subtle-text">{hasCustomCode ? customCodeNameHelp : randomCodeNameHelp}</span>
        </label>
        <div className="field-stack">
          <span>{codeLoginModeLabel}</span>
          <div className="segmented-control" role="group" aria-label={codeLoginModeLabel}>
            <button
              className={cx('segmented-option', !requiresWebPin && 'segmented-option-active')}
              type="button"
              onClick={() => setRequiresWebPin(false)}
            >
              {codeOnlyLabel}
            </button>
            <button
              className={cx('segmented-option', requiresWebPin && 'segmented-option-active')}
              type="button"
              onClick={() => setRequiresWebPin(true)}
            >
              {withPasswordLabel}
            </button>
          </div>
          <span className="subtle-text">{requiresWebPin ? withPasswordHelp : codeOnlyHelp}</span>
        </div>
        <div className="field-stack">
          <span>{t('permissions')}</span>
          <div className="permission-grid">
            {ALL_PERMISSION_KEYS.map((permission) => (
              <PermissionCheckbox
                key={permission}
                locale={locale}
                permission={permission}
                checked={selectedPermissions.includes(permission)}
                helpOpen={openPermissionHelp === permission}
                onToggle={() => togglePermission(permission)}
                onSetHelpOpen={(next) =>
                  setOpenPermissionHelp((current) => {
                    if (next) {
                      return permission;
                    }
                    return current === permission ? null : current;
                  })
                }
              />
            ))}
          </div>
        </div>
        <button className="primary-button" disabled={creatingCodes || !selectedServerId || effectiveCount < 1 || selectedPermissions.length === 0 || normalizedCustomCode.length > 32}>
          {creatingCodes ? <ButtonLoading label={t('loading')} /> : t('create')}
        </button>
      </form>
      {createdCodes.length > 0 ? (
        <div className="section-subpanel">
          <div className="field-label-row created-codes-header">
            <div>
              <h3>{t('plainCodes')}</h3>
              <p className="subtle-text">{t('createdCodesHelp')}</p>
            </div>
            {createdCodes.length >= 2 ? (
              <button
                className={cx(
                  createdCodes.every((item) => copiedInviteCodeIds.includes(item.inviteCodeId)) ? 'success-button' : 'ghost-button',
                  'field-action-button'
                )}
                type="button"
                onClick={() => {
                  copyText(createdCodes.map((item) => item.code).join('\n'));
                  setCopiedInviteCodeIds((current) =>
                    Array.from(new Set([...current, ...createdCodes.map((item) => item.inviteCodeId)]))
                  );
                }}
              >
                {copyAllCodesLabel}
              </button>
            ) : null}
          </div>
          <div className="stack-sm">
            {createdCodes.map((item) => {
              const directLoginUrl = buildDirectLoginUrl(item.code);
              return (
                <div key={item.inviteCodeId} className="created-code-item">
                  <div className="code-row">
                    <code>{item.code}</code>
                    <button
                      className={cx(copiedInviteCodeIds.includes(item.inviteCodeId) ? 'success-button' : 'ghost-button', 'copy-code-button')}
                      type="button"
                      onClick={() => {
                        copyText(item.code);
                        setCopiedInviteCodeIds((current) =>
                          current.includes(item.inviteCodeId) ? current : [...current, item.inviteCodeId]
                        );
                      }}
                    >
                      Copy
                    </button>
                  </div>
                  {!item.requiresWebPin ? (
                    <div className="code-row code-row-direct-link">
                      <div className="direct-link-value">
                        <span className="subtle-text">{directLinkLabel}</span>
                        <code>{directLoginUrl}</code>
                      </div>
                      <button
                        className={cx(
                          copiedDirectLinkInviteCodeIds.includes(item.inviteCodeId) ? 'success-button' : 'ghost-button',
                          'copy-code-button'
                        )}
                        type="button"
                        onClick={() => {
                          copyText(directLoginUrl);
                          setCopiedDirectLinkInviteCodeIds((current) =>
                            current.includes(item.inviteCodeId) ? current : [...current, item.inviteCodeId]
                          );
                        }}
                      >
                        {copyLinkLabel}
                      </button>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </SectionCard>
  );
}

function InviteCheckSection({ selectedServerId }: { selectedServerId: number | null }) {
  const { locale, t } = useI18n();
  const [codeCheckInput, setCodeCheckInput] = useState('');
  const [checkingCode, setCheckingCode] = useState(false);
  const [checkedInvites, setCheckedInvites] = useState<InviteCodeCheckBatchItem[]>([]);
  const [checkInfo, setCheckInfo] = useState<ApiInfo | null>(null);
  const codeCheckEntries = useMemo(() => splitCodeCheckInput(codeCheckInput), [codeCheckInput]);
  const checkedInvite = checkedInvites.length === 1 ? checkedInvites[0].invite : null;
  const showBatchCheckResults = checkedInvites.length > 1 || (checkedInvites.length === 1 && !checkedInvites[0].found);
  const checkedInviteFoundCount = checkedInvites.filter((item) => item.found).length;
  const checkedInviteMissingCount = checkedInvites.length - checkedInviteFoundCount;
  const checkCodeButtonLabel = locale === 'ru' ? 'Проверить' : 'Check';
  const codeFoundMessage = locale === 'ru' ? 'Код найден.' : 'Code found.';
  const inputTypeLabel = locale === 'ru' ? 'Тип ввода' : 'Input type';
  const codeCheckTextareaPlaceholder =
    locale === 'ru' ? 'Код или SHA-256, по одному на строку' : 'One code or SHA-256 per line';
  const codeCheckHelpText =
    locale === 'ru'
      ? 'Можно вставить один или несколько кодов: каждая непустая строка проверяется отдельно.'
      : 'Paste one or more codes. Each non-empty line is checked separately.';
  const codesFoundMessage =
    locale === 'ru'
      ? `Найдено ${checkedInviteFoundCount} из ${checkedInvites.length}.`
      : `Found ${checkedInviteFoundCount} of ${checkedInvites.length}.`;
  const tooManyCodesMessage =
    locale === 'ru'
      ? `За раз можно проверить не больше ${MAX_CODE_CHECK_INPUTS} кодов.`
      : `You can check up to ${MAX_CODE_CHECK_INPUTS} codes at once.`;
  const notFoundLabel = locale === 'ru' ? 'Не найден' : 'Not found';
  const inputLabel = locale === 'ru' ? 'Ввод' : 'Input';
  const codeHashLabel = locale === 'ru' ? 'SHA-256' : 'SHA-256';
  const loginModeLabel = locale === 'ru' ? '\u0412\u0445\u043e\u0434' : 'Login';
  const passwordRequiredLabel = locale === 'ru' ? '\u041f\u0430\u0440\u043e\u043b\u044c' : 'Password';
  const codeOnlyInviteLabel = locale === 'ru' ? '\u0411\u0435\u0437 \u043f\u0430\u0440\u043e\u043b\u044f' : 'Code only';
  const labelText = locale === 'ru' ? 'Описание' : 'Label';

  const handleCheckCode = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (checkingCode) {
      return;
    }

    const codes = splitCodeCheckInput(codeCheckInput);
    if (codes.length > MAX_CODE_CHECK_INPUTS) {
      setCheckedInvites([]);
      setCheckInfo({ message: tooManyCodesMessage, tone: 'error' });
      return;
    }

    setCheckingCode(true);
    setCheckInfo(null);
    try {
      const result = await api.post<InviteCodeCheckBatchResponse>('/api/admin/invite-codes/check-batch', {
        serverId: selectedServerId,
        codes
      });
      setCheckedInvites(result.results);
      setCheckInfo({
        message:
          result.results.length === 1 && result.missingCount === 0
            ? codeFoundMessage
            : locale === 'ru'
              ? `Найдено ${result.foundCount} из ${result.results.length}.`
              : `Found ${result.foundCount} of ${result.results.length}.`,
        tone: result.foundCount > 0 ? 'success' : 'error'
      });
    } catch (error) {
      setCheckedInvites([]);
      setCheckInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      setCheckingCode(false);
    }
  };

  return (
    <SectionCard title={t('codeChecking')} icon="search">
      <Alert info={checkInfo} />
      <form className="stack-md" onSubmit={handleCheckCode}>
        <label className="field-stack">
          <span>{codeCheckTextareaPlaceholder}</span>
          <textarea
            className="textarea-input code-check-textarea"
            value={codeCheckInput}
            onChange={(event) => setCodeCheckInput(event.target.value)}
            placeholder={codeCheckTextareaPlaceholder}
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        <div className="subtle-text">{codeCheckHelpText}</div>
        <button className="primary-button" disabled={checkingCode || codeCheckEntries.length < 1}>
          {checkingCode ? <ButtonLoading label={t('loading')} /> : checkCodeButtonLabel}
        </button>
      </form>

      {checkedInvite ? (
        <div className="section-subpanel invite-check-result">
          <div className="field-label-row">
            <div>
              <h3>#{checkedInvite.inviteCodeId}</h3>
              <div className="subtle-text">{checkedInvite.serverName ?? t('global')}</div>
            </div>
            <span className={badgeClass(checkedInvite.revokedAt ? 'REVOKED' : checkedInvite.activatedAt ? 'APPROVED' : 'PENDING')}>
              {checkedInvite.revokedAt ? t('revoked') : checkedInvite.activatedAt ? t('activated') : t('inactive')}
            </span>
          </div>
          <div className="details-grid compact-grid">
            <DetailItem label={codeHashLabel} value={checkedInvite.codeHashPreview} />
            <DetailItem
              label={inputTypeLabel}
              value={checkedInvite.inputType === 'sha256' ? 'SHA-256' : locale === 'ru' ? 'Исходный код' : 'Plain code'}
            />
            <DetailItem label={loginModeLabel} value={checkedInvite.requiresWebPin ? passwordRequiredLabel : codeOnlyInviteLabel} />
            <DetailItem label={labelText} value={checkedInvite.label ?? t('notSet')} />
            <DetailItem label={t('createdAt')} value={formatDate(locale, checkedInvite.createdAt)} />
            <DetailItem label={t('activatedAt')} value={formatDate(locale, checkedInvite.activatedAt)} />
            <DetailItem label={t('revokedAt')} value={formatDate(locale, checkedInvite.revokedAt)} />
            <DetailItem label={t('reason')} value={checkedInvite.revokedReason ?? t('notSet')} />
            <DetailItem label={t('profileId')} value={checkedInvite.profileId == null ? t('notSet') : String(checkedInvite.profileId)} />
            <DetailItem label={t('nickname')} value={checkedInvite.boundName ?? t('notSet')} />
            <DetailItem label={t('lastLoginAt')} value={formatDate(locale, checkedInvite.lastLoginAt)} />
            <DetailItem label={t('creator')} value={checkedInvite.creatorName ?? 'System'} />
          </div>
          <div className="tag-cluster">
            {checkedInvite.permissions.map((permission) => (
              <span key={permission} className="tag-chip">
                {permissionLabel(locale, permission)}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {showBatchCheckResults ? (
        <div className="section-subpanel invite-check-result invite-check-batch-result">
          <div className="field-label-row">
            <div>
              <h3>{codesFoundMessage}</h3>
              <div className="subtle-text">
                {checkedInviteMissingCount > 0 ? `${notFoundLabel}: ${checkedInviteMissingCount}` : t('success')}
              </div>
            </div>
          </div>
          <div className="table-wrapper invite-check-table-wrapper">
            <table className="data-table invite-check-table">
              <thead>
                <tr>
                  <th>{inputLabel}</th>
                  <th>{t('inviteCodeId')}</th>
                  <th>{t('status')}</th>
                  <th>{loginModeLabel}</th>
                  <th>{t('server')}</th>
                  <th>{t('nickname')}</th>
                  <th>{labelText}</th>
                  <th>{t('createdAt')}</th>
                </tr>
              </thead>
              <tbody>
                {checkedInvites.map((result, index) => {
                  const invite = result.invite;
                  const statusKind = invite
                    ? invite.revokedAt
                      ? 'REVOKED'
                      : invite.activatedAt
                        ? 'APPROVED'
                        : 'PENDING'
                    : 'REJECTED';
                  return (
                    <tr key={`${result.input}-${index}`}>
                      <td className="invite-check-input-cell">
                        <code>{result.input}</code>
                        <div className="subtle-text">
                          {result.inputType === 'sha256' ? 'SHA-256' : locale === 'ru' ? 'Исходный код' : 'Plain code'}
                        </div>
                      </td>
                      <td>
                        {invite ? (
                          <>
                            #{invite.inviteCodeId}
                            <div className="subtle-text">{invite.codeHashPreview}</div>
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td>
                        <span className={badgeClass(statusKind)}>
                          {invite ? (invite.revokedAt ? t('revoked') : invite.activatedAt ? t('activated') : t('inactive')) : notFoundLabel}
                        </span>
                      </td>
                      <td>{invite ? (invite.requiresWebPin ? passwordRequiredLabel : codeOnlyInviteLabel) : '-'}</td>
                      <td>{invite ? invite.serverName ?? t('global') : '-'}</td>
                      <td>{invite ? invite.boundName ?? t('notSet') : '-'}</td>
                      <td>{invite ? invite.label ?? t('notSet') : '-'}</td>
                      <td>{invite ? formatDate(locale, invite.createdAt) : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </SectionCard>
  );
}

function InviteListSection({ user, selectedServerId }: { user: SessionUser; selectedServerId: number | null }) {
  const { locale, t } = useI18n();
  const [items, setItems] = useState<InviteCodeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<ApiInfo | null>(null);
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const searchSuggestionsId = useId();
  const [state, setState] = useState<'all' | 'activated' | 'inactive' | 'revoked'>('all');
  const [openPermissionHelp, setOpenPermissionHelp] = useState<string | null>(null);
  const [editingInviteId, setEditingInviteId] = useState<number | null>(null);
  const [editingPermissions, setEditingPermissions] = useState<string[]>([]);
  const [savingInvitePermissions, setSavingInvitePermissions] = useState(false);
  const [revokingInviteId, setRevokingInviteId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const skipNextInvitePageLoadRef = useRef(false);
  const nicknameCacheKey = useMemo(() => `invites:${selectedServerId ?? 'all'}`, [selectedServerId]);
  const [cachedNicknames, setCachedNicknames] = useState<string[]>(() => getCachedNicknames(nicknameCacheKey));
  const nicknameSuggestions = useMemo(() => buildNicknameSuggestions(cachedNicknames, search), [cachedNicknames, search]);
  const editingInvite = useMemo(
    () => items.find((item) => item.inviteCodeId === editingInviteId) ?? null,
    [editingInviteId, items]
  );

  useEffect(() => {
    setCachedNicknames(getCachedNicknames(nicknameCacheKey));
  }, [nicknameCacheKey]);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (event.target instanceof Element && event.target.closest('.permission-help')) {
        return;
      }

      setOpenPermissionHelp(null);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  const load = async (pageToLoad = page, options: LoadOptions = {}) => {
    const showLoading = options.showLoading ?? true;
    if (showLoading) {
      setLoading(true);
    }
    try {
      const query = new URLSearchParams();
      if (selectedServerId) query.set('serverId', String(selectedServerId));
      if (search.trim()) query.set('search', search.trim());
      if (state !== 'all') query.set('state', state);
      appendPaginationParams(query, pageToLoad);
      const payload = await api.get<InviteCodeItem[]>(`/api/admin/invite-codes?${query.toString()}`);
      const visibleItems = pageItems(payload);
      setItems(visibleItems);
      setHasNext(hasNextPage(payload));
      setCachedNicknames(mergeCachedNicknames(nicknameCacheKey, payload.map((item) => item.boundName)));
      setInfo(keepSuccessInfo);
      if (editingInviteId != null) {
        const refreshedInvite = visibleItems.find((item) => item.inviteCodeId === editingInviteId) ?? null;
        if (!refreshedInvite || refreshedInvite.revokedAt || refreshedInvite.isGlobalMaster) {
          setEditingInviteId(null);
          setEditingPermissions([]);
        } else {
          setEditingPermissions(refreshedInvite.permissions);
        }
      }
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    setPage(1);
    void load(1);
  }, [selectedServerId, state]);

  useEffect(() => {
    if (skipNextInvitePageLoadRef.current) {
      skipNextInvitePageLoadRef.current = false;
      return;
    }

    void load(page);
  }, [page]);

  const handleSearch = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (searching) {
      return;
    }

    setSearching(true);
    try {
      if (page !== 1) {
        skipNextInvitePageLoadRef.current = true;
        setPage(1);
      }
      await load(1, { showLoading: false });
    } finally {
      setSearching(false);
    }
  };

  const toggleEditingPermission = (permission: string) => {
    setEditingPermissions((current) =>
      current.includes(permission) ? current.filter((value) => value !== permission) : [...current, permission]
    );
  };

  const canEditPermissions = hasPermission(user, 'CREATE_INVITES');
  const canRevokeInvite = hasPermission(user, 'BAN_BY_CODE');
  const editPermissionsTitle = locale === 'ru' ? 'Редактирование прав кода' : 'Edit invite permissions';
  const savePermissionsLabel = locale === 'ru' ? 'Сохранить права' : 'Save permissions';
  const editInviteLabel = locale === 'ru' ? 'Изменить права' : 'Edit permissions';
  const revokeInviteLabel = locale === 'ru' ? 'Отозвать' : 'Revoke';
  const permissionsSavedMessage = locale === 'ru' ? 'Права кода обновлены.' : 'Invite permissions updated.';
  const revokeReason = locale === 'ru' ? 'Код отозван администратором' : 'Invite revoked by administrator';
  const inviteLoginModeColumnLabel = locale === 'ru' ? '\u0412\u0445\u043e\u0434' : 'Login';
  const passwordRequiredLabel = locale === 'ru' ? '\u041f\u0430\u0440\u043e\u043b\u044c' : 'Password';
  const codeOnlyInviteLabel = locale === 'ru' ? '\u0411\u0435\u0437 \u043f\u0430\u0440\u043e\u043b\u044f' : 'Code only';
  const inviteTableColumnCount = canEditPermissions || canRevokeInvite ? 9 : 8;
  const revokeConfirmMessage =
    locale === 'ru'
      ? 'Отозвать этот код доступа? Его активные сессии будут закрыты.'
      : 'Revoke this invite code? Its active sessions will be revoked.';

  const startInviteEditing = (item: InviteCodeItem) => {
    setEditingInviteId((current) => {
      if (current === item.inviteCodeId) {
        setEditingPermissions([]);
        return null;
      }

      setEditingPermissions(item.permissions);
      return item.inviteCodeId;
    });
  };

  const handleSaveInvitePermissions = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingInvite || savingInvitePermissions) {
      return;
    }

    setSavingInvitePermissions(true);
    try {
      await api.put(`/api/admin/invite-codes/${editingInvite.inviteCodeId}/permissions`, {
        serverId: selectedServerId,
        permissions: editingPermissions
      });
      setInfo({ message: permissionsSavedMessage, tone: 'success' });
      await load(page, { showLoading: false });
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      setSavingInvitePermissions(false);
    }
  };

  const handleRevokeInvite = async (item: InviteCodeItem) => {
    if (revokingInviteId != null) {
      return;
    }
    if (!window.confirm(revokeConfirmMessage)) {
      return;
    }

    setRevokingInviteId(item.inviteCodeId);
    try {
      await api.post('/api/admin/ban-invite', {
        serverId: selectedServerId,
        inviteCodeId: item.inviteCodeId,
        reason: revokeReason,
        kickIfOnline: false
      });
      setInfo({ message: locale === 'ru' ? 'Код отозван.' : 'Invite code revoked.', tone: 'success' });
      await load(page, { showLoading: false });
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      setRevokingInviteId(null);
    }
  };

  return (
    <SectionCard
      title={t('codeList')}
      icon="ticket"
      actions={
        <RefreshButton onClick={() => load(page, { showLoading: false })} label={t('refreshSection')} />
      }
    >
      <Alert info={info} />
      <form
        className="toolbar-row search-toolbar"
        onSubmit={handleSearch}
      >
        <div className="search-input-wrap">
          <input
            className="text-input search-input"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('search')}
            list={nicknameSuggestions.length > 0 ? searchSuggestionsId : undefined}
            autoComplete="off"
          />
          {nicknameSuggestions.length > 0 ? (
            <datalist id={searchSuggestionsId}>
              {nicknameSuggestions.map((nickname) => (
                <option key={nickname} value={nickname} />
              ))}
            </datalist>
          ) : null}
          <button className="ghost-button search-input-button" type="submit" disabled={searching} aria-label={t('search')} title={t('search')}>
            {searching ? <ButtonLoading label={t('loading')} /> : <SvgIcon name="search" className="button-svg-icon" />}
          </button>
        </div>
        <select className="select-input search-state-select" value={state} onChange={(event) => setState(event.target.value as typeof state)}>
          <option value="all">{t('all')}</option>
          <option value="activated">{t('activated')}</option>
          <option value="inactive">{t('inactive')}</option>
          <option value="revoked">{t('revoked')}</option>
        </select>
      </form>

        {loading ? (
          <LoadingInline />
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="table-wrapper invites-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('inviteCodeId')}</th>
                  <th>{t('server')}</th>
                  <th>{t('status')}</th>
                  <th>{inviteLoginModeColumnLabel}</th>
                  <th>{t('creator')}</th>
                  <th>{t('nickname')}</th>
                  <th>{t('permissions')}</th>
                  <th>{t('createdAt')}</th>
                  {canEditPermissions || canRevokeInvite ? <th>{t('actions')}</th> : null}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <Fragment key={item.inviteCodeId}>
                    <tr className={cx(editingInviteId === item.inviteCodeId && 'row-active')}>
                      <td className="invite-code-id-cell">
                        #{item.inviteCodeId}
                        <div className="subtle-text">{item.codeHashPreview.slice(0, 5)}</div>
                      </td>
                      <td>{item.serverName ?? '—'}</td>
                      <td>
                        <span className={badgeClass(item.revokedAt ? 'REVOKED' : item.activatedAt ? 'APPROVED' : 'PENDING')}>
                          {item.revokedAt ? t('revoked') : item.activatedAt ? t('activated') : t('inactive')}
                        </span>
                      </td>
                      <td>
                        <span className={cx('tag-chip', item.requiresWebPin ? 'tag-chip-muted' : 'tag-chip-success')}>
                          {item.requiresWebPin ? passwordRequiredLabel : codeOnlyInviteLabel}
                        </span>
                      </td>
                      <td>{item.creatorName ?? 'System'}</td>
                      <td>{item.boundName ?? '—'}</td>
                      <td>
                        <div className="tag-cluster">
                          {item.permissions.map((permission) => (
                            <span key={permission} className="tag-chip">
                              {permissionLabel(locale, permission)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>{formatDate(locale, item.createdAt)}</td>
                      {canEditPermissions || canRevokeInvite ? (
                        <td>
                          <div className="button-row invite-actions">
                            {canEditPermissions ? (
                              <button
                                className="ghost-button field-action-button"
                                type="button"
                                disabled={item.revokedAt != null || item.isGlobalMaster}
                                onClick={() => startInviteEditing(item)}
                              >
                                {editInviteLabel}
                              </button>
                            ) : null}
                            {canRevokeInvite ? (
                              <button
                                className="danger-button field-action-button"
                                type="button"
                                disabled={item.revokedAt != null || item.isGlobalMaster || revokingInviteId === item.inviteCodeId}
                                onClick={() => void handleRevokeInvite(item)}
                              >
                                {revokingInviteId === item.inviteCodeId ? <ButtonLoading label={t('loading')} /> : revokeInviteLabel}
                              </button>
                            ) : null}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                    {canEditPermissions && editingInviteId === item.inviteCodeId ? (
                      <tr className="invite-permissions-row">
                        <td colSpan={inviteTableColumnCount}>
                          <form className="stack-md section-subpanel invite-permissions-panel" onSubmit={handleSaveInvitePermissions}>
                            <div className="field-label-row">
                              <div>
                                <h3>{editPermissionsTitle}</h3>
                                <div className="subtle-text">
                                  #{item.inviteCodeId} · {item.boundName ?? item.label ?? item.creatorName ?? 'System'}
                                </div>
                              </div>
                              <button
                                className="ghost-button field-action-button"
                                type="button"
                                onClick={() => {
                                  setEditingInviteId(null);
                                  setEditingPermissions([]);
                                }}
                              >
                                {t('cancel')}
                              </button>
                            </div>

                            <div className="permission-grid">
                              {ALL_PERMISSION_KEYS.map((permission) => (
                                <PermissionCheckbox
                                  key={`edit-${permission}`}
                                  locale={locale}
                                  permission={permission}
                                  checked={editingPermissions.includes(permission)}
                                  helpOpen={openPermissionHelp === `edit:${permission}`}
                                  onToggle={() => toggleEditingPermission(permission)}
                                  onSetHelpOpen={(next) =>
                                    setOpenPermissionHelp((current) => {
                                      if (next) {
                                        return `edit:${permission}`;
                                      }
                                      return current === `edit:${permission}` ? null : current;
                                    })
                                  }
                                />
                              ))}
                            </div>

                            <div className="button-row">
                              <button className="primary-button" disabled={savingInvitePermissions || editingPermissions.length === 0}>
                                {savingInvitePermissions ? <ButtonLoading label={t('loading')} /> : savePermissionsLabel}
                              </button>
                            </div>
                          </form>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading ? <PaginationControls page={page} hasNext={hasNext} loading={loading} onPageChange={setPage} /> : null}
    </SectionCard>
  );
}

function PlayersSection({ user, selectedServerId }: { user: SessionUser; selectedServerId: number | null }) {
  const { locale, t } = useI18n();
  const [items, setItems] = useState<PlayerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<ApiInfo | null>(null);
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const searchSuggestionsId = useId();
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerItem | null>(null);
  const [newNickname, setNewNickname] = useState('');
  const [ipAddress, setIpAddress] = useState('');
  const [note, setNote] = useState('');
  const [resetReason, setResetReason] = useState('Authorization reset by administrator');
  const [banReason, setBanReason] = useState('Banned by administrator');
  const [kickIfOnline, setKickIfOnline] = useState(true);
  const [playerAction, setPlayerAction] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const skipNextPlayerPageLoadRef = useRef(false);
  const nicknameCacheKey = useMemo(() => `players:${selectedServerId ?? 'all'}`, [selectedServerId]);
  const [cachedNicknames, setCachedNicknames] = useState<string[]>(() => getCachedNicknames(nicknameCacheKey));
  const nicknameSuggestions = useMemo(() => buildNicknameSuggestions(cachedNicknames, search), [cachedNicknames, search]);

  useEffect(() => {
    setCachedNicknames(getCachedNicknames(nicknameCacheKey));
  }, [nicknameCacheKey]);

  const load = async (pageToLoad = page, options: LoadOptions = {}) => {
    const showLoading = options.showLoading ?? true;
    if (showLoading) {
      setLoading(true);
    }
    try {
      const query = new URLSearchParams();
      if (selectedServerId) query.set('serverId', String(selectedServerId));
      if (search.trim()) query.set('search', search.trim());
      appendPaginationParams(query, pageToLoad);
      const payload = await api.get<PlayerItem[]>(`/api/admin/players?${query.toString()}`);
      const visibleItems = pageItems(payload);
      setItems(visibleItems);
      setHasNext(hasNextPage(payload));
      setCachedNicknames(
        mergeCachedNicknames(
          nicknameCacheKey,
          payload.flatMap((item) => [item.nickname, item.displayName])
        )
      );
      setInfo(keepSuccessInfo);
      if (selectedPlayer) {
        const refreshed = visibleItems.find((item) => item.profileId === selectedPlayer.profileId) ?? null;
        setSelectedPlayer(refreshed);
      }
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    setPage(1);
    void load(1);
  }, [selectedServerId]);

  useEffect(() => {
    if (skipNextPlayerPageLoadRef.current) {
      skipNextPlayerPageLoadRef.current = false;
      return;
    }

    void load(page);
  }, [page]);

  const selectPlayer = (player: PlayerItem) => {
    setSelectedPlayer(player);
    setNewNickname(player.nickname ?? '');
  };

  const withTarget = () => ({
    serverId: selectedServerId,
    profileId: selectedPlayer?.profileId ?? null,
    inviteCodeId: selectedPlayer?.inviteCodeId ?? null
  });

  const runAction = async (actionId: string, path: string, body: Record<string, unknown>, successMessage = 'OK') => {
    if (playerAction) {
      return;
    }

    setPlayerAction(actionId);
    try {
      await api.post(path, body);
      setInfo({ message: successMessage, tone: 'success' });
      await load(page, { showLoading: false });
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      setPlayerAction(null);
    }
  };

  const handleSearch = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (searching) {
      return;
    }

    setSearching(true);
    try {
      if (page !== 1) {
        skipNextPlayerPageLoadRef.current = true;
        setPage(1);
      }
      await load(1, { showLoading: false });
    } finally {
      setSearching(false);
    }
  };

  const canReset = hasPermission(user, 'FORCE_BIND_IP');
  const canForceBind = hasPermission(user, 'FORCE_BIND_IP');
  const canForceNickname = hasPermission(user, 'FORCE_CHANGE_NICKNAME');
  const canResetPassword = hasPermission(user, 'FORCE_BIND_IP');
  const canBan = hasPermission(user, 'BAN_BY_CODE');
  const resetPasswordConfirmText =
    locale === 'ru'
      ? 'Сбросить пароль входа для этого кода? Активные сессии будут завершены.'
      : 'Reset login password for this invite code? Active sessions will be revoked.';

  return (
    <div className="page-grid two-columns-strong">
      <SectionCard
        title={t('players')}
        icon="users"
        actions={
          <form
            className="toolbar-row compact-toolbar search-toolbar"
            onSubmit={handleSearch}
          >
            <div className="search-input-wrap">
              <input
                className="text-input search-input"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('search')}
                list={nicknameSuggestions.length > 0 ? searchSuggestionsId : undefined}
                autoComplete="off"
              />
              {nicknameSuggestions.length > 0 ? (
                <datalist id={searchSuggestionsId}>
                  {nicknameSuggestions.map((nickname) => (
                    <option key={nickname} value={nickname} />
                  ))}
                </datalist>
              ) : null}
              <button className="ghost-button search-input-button" type="submit" disabled={searching} aria-label={t('search')} title={t('search')}>
                {searching ? <ButtonLoading label={t('loading')} /> : <SvgIcon name="search" className="button-svg-icon" />}
              </button>
            </div>
          </form>
        }
      >
        <Alert info={info} />
        {loading ? (
          <LoadingInline />
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="table-wrapper">
            <table className="data-table clickable-rows players-table">
              <thead>
                <tr>
                  <th>{t('profileId')}</th>
                  <th>{t('nickname')}</th>
                  <th>{t('skin')}</th>
                  <th>{t('lastSeenIp')}</th>
                  <th>{t('lastLoginAt')}</th>
                  <th>{t('inviter')}</th>
                  <th>{t('status')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.profileId}
                    className={cx(selectedPlayer?.profileId === item.profileId && 'row-active')}
                    onClick={() => selectPlayer(item)}
                  >
                    <td>#{item.profileId}</td>
                    <td>
                      <div>{item.nickname ?? item.displayName ?? '—'}</div>
                      <div className="subtle-text">Invite #{item.inviteCodeId}</div>
                    </td>
                    <td>
                      <PlayerSkinInfo skin={item.skin} compact />
                    </td>
                    <td>{maskIpAddress(item.lastSeenIp) ?? '—'}</td>
                    <td>{formatDate(locale, item.lastLoginAt)}</td>
                    <td>{item.inviterName ?? 'System'}</td>
                    <td>
                      <span className={badgeClass(item.revokedAt ? 'REVOKED' : 'ACTIVE')}>
                        {item.revokedAt ? t('revoked') : t('active')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading ? <PaginationControls page={page} hasNext={hasNext} loading={loading} onPageChange={setPage} /> : null}
      </SectionCard>

      <div className="column-stack">
        <SectionCard title={t('openAdminTools')} icon="gear">
          <p className="subtle-text">{t('selectPlayerHint')}</p>
          {selectedPlayer ? (
            <div className="details-grid compact-grid">
              <DetailItem label={t('profileId')} value={String(selectedPlayer.profileId)} />
              <DetailItem label={t('inviteCodeId')} value={String(selectedPlayer.inviteCodeId)} />
              <DetailItem label={t('nickname')} value={selectedPlayer.nickname ?? '—'} />
              <DetailItem label={t('lastSeenIp')} value={maskIpAddress(selectedPlayer.lastSeenIp) ?? '—'} />
              <DetailItem label={t('skin')} value={<PlayerSkinInfo skin={selectedPlayer.skin} />} />
              <DetailItem
                label={locale === 'ru' ? 'Скин обновлен' : 'Skin updated'}
                value={formatDate(locale, selectedPlayer.skin?.updatedAt ?? null)}
              />
            </div>
          ) : (
            <EmptyState />
          )}
        </SectionCard>

        {canForceNickname ? (
          <SectionCard title={t('forceChangeNickname')} icon="edit">
            <form
              className="stack-md"
              onSubmit={(event) => {
                event.preventDefault();
                if (!selectedPlayer) return;
                void runAction(
                  'force-nickname',
                  '/api/admin/force-change-nickname',
                  {
                    ...withTarget(),
                    nickname: newNickname
                  },
                  t('formSaved')
                );
              }}
            >
              <label className="field-stack">
                <span>{t('nickname')}</span>
                <input className="text-input" value={newNickname} onChange={(event) => setNewNickname(event.target.value)} />
              </label>
              <button className="primary-button" disabled={playerAction != null || !selectedPlayer || newNickname.trim().length < 3}>
                {playerAction === 'force-nickname' ? <ButtonLoading label={t('loading')} /> : t('save')}
              </button>
            </form>
          </SectionCard>
        ) : null}

        {canForceBind ? (
          <SectionCard title={t('forceBindIp')} icon="shieldCheck">
            <form
              className="stack-md"
              onSubmit={(event) => {
                event.preventDefault();
                if (!selectedPlayer) return;
                void runAction(
                  'force-bind-ip',
                  '/api/admin/force-bind-ip',
                  {
                    ...withTarget(),
                    ipAddress,
                    note
                  },
                  t('formSaved')
                );
              }}
            >
              <label className="field-stack">
                <span>{t('ipAddress')}</span>
                <input className="text-input" value={ipAddress} onChange={(event) => setIpAddress(event.target.value)} />
              </label>
              <label className="field-stack">
                <span>{t('note')}</span>
                <input className="text-input" value={note} onChange={(event) => setNote(event.target.value)} />
              </label>
              <button className="primary-button" disabled={playerAction != null || !selectedPlayer || ipAddress.trim().length < 3}>
                {playerAction === 'force-bind-ip' ? <ButtonLoading label={t('loading')} /> : t('save')}
              </button>
            </form>
          </SectionCard>
        ) : null}

        {canReset ? (
          <SectionCard title={t('resetAuthorizedIps')} icon="refresh">
            <form
              className="stack-md"
              onSubmit={(event) => {
                event.preventDefault();
                if (!selectedPlayer) return;
                if (!window.confirm('Reset authorized IPs for this player?')) return;
                void runAction(
                  'reset-authorized-ips',
                  '/api/admin/reset-authorized-ips',
                  {
                    ...withTarget(),
                    reason: resetReason,
                    kickIfOnline
                  },
                  t('formSaved')
                );
              }}
            >
              <label className="field-stack">
                <span>{t('reason')}</span>
                <input className="text-input" value={resetReason} onChange={(event) => setResetReason(event.target.value)} />
              </label>
              <label className="checkbox-row">
                <input type="checkbox" checked={kickIfOnline} onChange={(event) => setKickIfOnline(event.target.checked)} />
                <span>{t('kickIfOnline')}</span>
              </label>
              <button className="danger-button" disabled={playerAction != null || !selectedPlayer}>
                {playerAction === 'reset-authorized-ips' ? <ButtonLoading label={t('loading')} /> : t('resetAuthorizedIps')}
              </button>
            </form>
          </SectionCard>
        ) : null}

        {canResetPassword ? (
          <SectionCard title={t('resetLoginPassword')} icon="key">
            <form
              className="stack-md"
              onSubmit={(event) => {
                event.preventDefault();
                if (!selectedPlayer) return;
                if (!window.confirm(resetPasswordConfirmText)) return;
                void runAction(
                  'reset-login-password',
                  '/api/admin/reset-login-password',
                  {
                    ...withTarget()
                  },
                  t('formSaved')
                );
              }}
            >
              <button className="danger-button" disabled={playerAction != null || !selectedPlayer}>
                {playerAction === 'reset-login-password' ? <ButtonLoading label={t('loading')} /> : t('resetLoginPassword')}
              </button>
            </form>
          </SectionCard>
        ) : null}

        {canBan ? (
          <SectionCard title={t('banByCode')} icon="alertTriangle">
            <form
              className="stack-md"
              onSubmit={(event) => {
                event.preventDefault();
                if (!selectedPlayer) return;
                if (!window.confirm('Ban this invite code and revoke all sessions?')) return;
                void runAction(
                  'ban-by-code',
                  '/api/admin/ban-invite',
                  {
                    serverId: selectedServerId,
                    inviteCodeId: selectedPlayer.inviteCodeId,
                    reason: banReason,
                    kickIfOnline
                  },
                  t('formSaved')
                );
              }}
            >
              <label className="field-stack">
                <span>{t('reason')}</span>
                <input className="text-input" value={banReason} onChange={(event) => setBanReason(event.target.value)} />
              </label>
              <label className="checkbox-row">
                <input type="checkbox" checked={kickIfOnline} onChange={(event) => setKickIfOnline(event.target.checked)} />
                <span>{t('kickIfOnline')}</span>
              </label>
              <button className="danger-button" disabled={playerAction != null || !selectedPlayer}>
                {playerAction === 'ban-by-code' ? <ButtonLoading label={t('loading')} /> : t('banByCode')}
              </button>
            </form>
          </SectionCard>
        ) : null}
      </div>
    </div>
  );
}

function AdminPendingSection({
  selectedServerId,
  onPendingCountChange
}: {
  selectedServerId: number | null;
  onPendingCountChange?: (value: number) => void;
}) {
  const { locale, t } = useI18n();
  const [items, setItems] = useState<AdminPendingAuthItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('PENDING');
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);

  const load = async (pageToLoad = page, options: LoadOptions = {}) => {
    const showLoading = options.showLoading ?? true;
    if (showLoading) {
      setLoading(true);
    }
    try {
      const query = new URLSearchParams();
      if (selectedServerId) {
        query.set('serverId', String(selectedServerId));
      }
      if (status) {
        query.set('status', status);
      }
      appendPaginationParams(query, pageToLoad);

      const pendingQuery = new URLSearchParams();
      pendingQuery.set('status', 'PENDING');
      pendingQuery.set('limit', '200');
      if (selectedServerId) {
        pendingQuery.set('serverId', String(selectedServerId));
      }

      const [payload, pendingPayload] = await Promise.all([
        api.get<AdminPendingAuthItem[]>(`/api/admin/pending-auth?${query.toString()}`),
        onPendingCountChange ? api.get<AdminPendingAuthItem[]>(`/api/admin/pending-auth?${pendingQuery.toString()}`) : Promise.resolve(null)
      ]);

      setItems(pageItems(payload));
      setHasNext(hasNextPage(payload));
      if (pendingPayload) {
        onPendingCountChange?.(pendingPayload.length);
      }
    } catch {
      onPendingCountChange?.(0);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    setPage(1);
    void load(1);
  }, [selectedServerId, status]);

  useEffect(() => {
    void load(page);
  }, [page]);

  return (
    <SectionCard
      title={t('adminPending')}
      icon="clipboard"
      actions={
        <div className="toolbar-row compact-toolbar admin-pending-toolbar">
          <RefreshButton onClick={() => load(page, { showLoading: false })} label={t('refreshSection')} />
          <select
            className="select-input admin-pending-status-select"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            <option value="">{t('all')}</option>
            <option value="PENDING">{t('requestStatusPending')}</option>
            <option value="APPROVED">{t('requestStatusApproved')}</option>
            <option value="REJECTED">{t('requestStatusRejected')}</option>
            <option value="EXPIRED">{t('requestStatusExpired')}</option>
            <option value="CANCELLED">{t('requestStatusCancelled')}</option>
          </select>
        </div>
      }
    >
      {loading ? (
        <LoadingInline />
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>{t('server')}</th>
                <th>{t('nickname')}</th>
                <th>{t('ipAddress')}</th>
                <th>{t('status')}</th>
                <th>{t('createdAt')}</th>
                <th>{t('expiresAt')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>#{item.id}</td>
                  <td>{item.serverName ?? '—'}</td>
                  <td>{item.nickname ?? item.displayName ?? '—'}</td>
                  <td>{maskIpAddress(item.ipAddress) ?? '—'}</td>
                  <td>
                    <span className={badgeClass(item.status)}>{humanStatus(t, item.status)}</span>
                  </td>
                  <td>{formatDate(locale, item.createdAt)}</td>
                  <td>{formatDate(locale, item.expiresAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading ? <PaginationControls page={page} hasNext={hasNext} loading={loading} onPageChange={setPage} /> : null}
    </SectionCard>
  );
}

function BroadcastSection({ user, selectedServerId }: { user: SessionUser; selectedServerId: number | null }) {
  const { locale, t } = useI18n();
  const [items, setItems] = useState<AdminNotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<ApiInfo | null>(null);
  const [scope, setScope] = useState<'SERVER' | 'GLOBAL'>(user.isGlobalMaster ? 'SERVER' : 'SERVER');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetNickname, setTargetNickname] = useState('');
  const [severity, setSeverity] = useState<'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR'>('INFO');
  const neverPresetValue = toDateTimeLocal(new Date(MAX_INT_UNIX_SECONDS * 1_000));
  const [expiresAt, setExpiresAt] = useState(neverPresetValue);
  const [selectedAttachments, setSelectedAttachments] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [deletingNotificationId, setDeletingNotificationId] = useState<number | null>(null);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<number | null>(null);
  const [editingNotification, setEditingNotification] = useState<AdminNotificationItem | null>(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const skipNextBroadcastPageLoadRef = useRef(false);
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);
  const formServerId = editingNotification?.serverId ?? selectedServerId;

  function resetForm() {
    setEditingNotification(null);
    setScope('SERVER');
    setTitle('');
    setBody('');
    setTargetNickname('');
    setSeverity('INFO');
    setExpiresAt(neverPresetValue);
    setSelectedAttachments([]);
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  }

  const load = async (pageToLoad = page, options: LoadOptions = {}) => {
    const showLoading = options.showLoading ?? true;
    if (showLoading) {
      setLoading(true);
    }
    try {
      const query = new URLSearchParams();
      if (selectedServerId) query.set('serverId', String(selectedServerId));
      appendPaginationParams(query, pageToLoad);
      const payload = await api.get<AdminNotificationItem[]>(`/api/admin/notifications?${query.toString()}`);
      setItems(pageItems(payload));
      setHasNext(hasNextPage(payload));
      setInfo(keepSuccessInfo);
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    resetForm();
    setPage(1);
    void load(1);
  }, [selectedServerId]);

  useEffect(() => {
    if (skipNextBroadcastPageLoadRef.current) {
      skipNextBroadcastPageLoadRef.current = false;
      return;
    }

    void load(page);
  }, [page]);

  const handleEdit = (item: AdminNotificationItem) => {
    setEditingNotification(item);
    setScope(item.scope);
    setTitle(item.title);
    setBody(item.body);
    setTargetNickname(item.targetNickname ?? '');
    setSeverity(item.severity);
    setExpiresAt(item.expiresAt ? toDateTimeLocal(new Date(item.expiresAt)) : '');
    setSelectedAttachments([]);
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
    setInfo(null);
  };

  const handleAttachmentChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.currentTarget.files ?? []);
    if (files.length === 0) {
      setSelectedAttachments([]);
      return;
    }

    const existingCount = editingNotification?.attachments?.length ?? 0;
    if (existingCount + files.length > MAX_NOTIFICATION_ATTACHMENT_COUNT) {
      setInfo({
        message:
          locale === 'ru'
            ? `Можно прикрепить не больше ${MAX_NOTIFICATION_ATTACHMENT_COUNT} файлов к одному уведомлению.`
            : `A notification can have up to ${MAX_NOTIFICATION_ATTACHMENT_COUNT} attachments.`,
        tone: 'error'
      });
      event.currentTarget.value = '';
      setSelectedAttachments([]);
      return;
    }

    const oversizedFile = files.find((file) => file.size > MAX_NOTIFICATION_ATTACHMENT_BYTES);
    if (oversizedFile) {
      setInfo({
        message:
          locale === 'ru'
            ? `Файл "${oversizedFile.name}" слишком большой. Лимит: ${formatFileSize(locale, MAX_NOTIFICATION_ATTACHMENT_BYTES)}.`
            : `File "${oversizedFile.name}" is too large. Limit: ${formatFileSize(locale, MAX_NOTIFICATION_ATTACHMENT_BYTES)}.`,
        tone: 'error'
      });
      event.currentTarget.value = '';
      setSelectedAttachments([]);
      return;
    }

    setSelectedAttachments(files);
    setInfo(null);
  };

  const clearSelectedAttachments = () => {
    setSelectedAttachments([]);
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  };

  const buildNotificationPayload = () => {
    const payload = {
      serverId: formServerId,
      scope,
      title,
      body,
      targetNickname: targetNickname.trim() || null,
      severity,
      expiresAt: expiresAt || null
    };

    if (selectedAttachments.length === 0) {
      return payload;
    }

    const formData = new FormData();
    formData.set('serverId', payload.serverId == null ? '' : String(payload.serverId));
    formData.set('scope', payload.scope);
    formData.set('title', payload.title);
    formData.set('body', payload.body);
    formData.set('targetNickname', payload.targetNickname ?? '');
    formData.set('severity', payload.severity);
    formData.set('expiresAt', payload.expiresAt ?? '');
    for (const file of selectedAttachments) {
      formData.append('attachments', file, file.name);
    }
    return formData;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmedTargetNickname = targetNickname.trim();
    if (trimmedTargetNickname && scope !== 'SERVER') {
      setInfo({
        message:
          locale === 'ru'
            ? 'Персональное уведомление доступно только в области "По серверу".'
            : 'Targeted notification is available only in "Server" scope.',
        tone: 'error'
      });
      return;
    }

    if (sending) {
      return;
    }

    setSending(true);
    try {
      const payload = buildNotificationPayload();
      const wasEditing = editingNotification != null;

      if (editingNotification) {
        await api.put(`/api/admin/notifications/${editingNotification.id}`, payload);
      } else {
        await api.post('/api/admin/notifications', payload);
      }

      if (!wasEditing && page !== 1) {
        skipNextBroadcastPageLoadRef.current = true;
        setPage(1);
      }
      resetForm();
      setInfo({ message: wasEditing ? t('formSaved') : t('formCreated'), tone: 'success' });
      await load(wasEditing ? page : 1, { showLoading: false });
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (item: AdminNotificationItem) => {
    if (!window.confirm(t('deleteNotificationConfirm'))) {
      return;
    }

    if (deletingNotificationId != null) {
      return;
    }

    setDeletingNotificationId(item.id);
    setInfo(null);
    try {
      await api.delete(`/api/admin/notifications/${item.id}`);
      if (editingNotification?.id === item.id) {
        resetForm();
      }

      const nextPage = page > 1 && items.length <= 1 ? page - 1 : page;
      if (nextPage !== page) {
        skipNextBroadcastPageLoadRef.current = true;
        setPage(nextPage);
      }

      await load(nextPage, { showLoading: false });
      setInfo({ message: t('notificationDeleted'), tone: 'success' });
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      setDeletingNotificationId(null);
    }
  };

  const handleDeleteAttachment = async (item: AdminNotificationItem, attachment: NotificationAttachmentItem) => {
    if (
      !window.confirm(
        locale === 'ru'
          ? `Удалить вложение "${attachment.fileName}"?`
          : `Delete attachment "${attachment.fileName}"?`
      )
    ) {
      return;
    }

    if (deletingAttachmentId != null) {
      return;
    }

    setDeletingAttachmentId(attachment.id);
    setInfo(null);
    try {
      await api.delete(`/api/admin/notifications/${item.id}/attachments/${attachment.id}`);
      const removeAttachment = (notification: AdminNotificationItem) =>
        notification.id === item.id
          ? {
              ...notification,
              attachments: notification.attachments.filter((current) => current.id !== attachment.id)
            }
          : notification;
      setItems((current) => current.map(removeAttachment));
      setEditingNotification((current) => (current ? removeAttachment(current) : current));
      setInfo({ message: t('formSaved'), tone: 'success' });
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  return (
    <div className="page-grid two-columns">
    <SectionCard title={editingNotification ? t('editNotification') : t('sendNotification')} icon="megaphone">
      <Alert info={info} />
      <form className="stack-md" onSubmit={handleSubmit}>
        {editingNotification ? (
          <div className="meta-line">
            #{editingNotification.id} · {editingNotification.serverName ?? t('global')} · {formatDate(locale, editingNotification.createdAt)}
          </div>
        ) : null}
        {user.isGlobalMaster ? (
          <label className="field-stack">
            <span>{t('scope')}</span>
            <select className="select-input" value={scope} onChange={(event) => setScope(event.target.value as typeof scope)}>
              <option value="SERVER">{t('serverScope')}</option>
              <option value="GLOBAL">{t('global')}</option>
            </select>
          </label>
        ) : null}
        <label className="field-stack">
          <span>{t('targetNickname')}</span>
          <input
            className="text-input"
            value={targetNickname}
            onChange={(event) => setTargetNickname(event.target.value)}
            placeholder={locale === 'ru' ? 'Никнейм игрока (опционально)' : 'Player nickname (optional)'}
          />
          <span className="subtle-text">
            {locale === 'ru'
              ? 'Оставьте пустым, чтобы отправить всем игрокам в выбранной области.'
              : 'Leave empty to send to everyone in the selected scope.'}
          </span>
        </label>
        <label className="field-stack">
          <span>{t('title')}</span>
          <input className="text-input" value={title} onChange={(event) => setTitle(event.target.value)} />
        </label>
        <label className="field-stack">
          <span>{t('body')}</span>
          <textarea className="textarea-input large-textarea" value={body} onChange={(event) => setBody(event.target.value)} />
        </label>
        <label className="field-stack">
          <span>{locale === 'ru' ? 'Вложения' : 'Attachments'}</span>
          <input
            ref={attachmentInputRef}
            className="file-input"
            type="file"
            multiple
            onChange={handleAttachmentChange}
          />
          <span className="subtle-text">
            {locale === 'ru'
              ? `До ${MAX_NOTIFICATION_ATTACHMENT_COUNT} файлов, до ${formatFileSize(locale, MAX_NOTIFICATION_ATTACHMENT_BYTES)} каждый.`
              : `Up to ${MAX_NOTIFICATION_ATTACHMENT_COUNT} files, ${formatFileSize(locale, MAX_NOTIFICATION_ATTACHMENT_BYTES)} each.`}
          </span>
          {selectedAttachments.length > 0 ? (
            <div className="selected-attachments">
              {selectedAttachments.map((file) => (
                <div key={`${file.name}-${file.size}-${file.lastModified}`} className="selected-attachment-row">
                  <span className="selected-attachment-name">{file.name}</span>
                  <span className="selected-attachment-size">{formatFileSize(locale, file.size)}</span>
                </div>
              ))}
              <button className="ghost-button selected-attachments-clear" type="button" onClick={clearSelectedAttachments} disabled={sending}>
                {locale === 'ru' ? 'Убрать файлы' : 'Clear files'}
              </button>
            </div>
          ) : null}
        </label>
        <div className="inline-fields two-columns-inline">
          <label className="field-stack">
            <span>{t('severity')}</span>
            <select className="select-input" value={severity} onChange={(event) => setSeverity(event.target.value as typeof severity)}>
              <option value="INFO">{t('info')}</option>
              <option value="SUCCESS">{t('success')}</option>
              <option value="WARNING">{t('warning')}</option>
              <option value="ERROR">{t('error')}</option>
            </select>
          </label>
          <DateTimePresetField
            label={t('expiresAt')}
            value={expiresAt}
            onChange={setExpiresAt}
            locale={locale}
            emptyLabel={t('notSet')}
            presets={[
              {
                id: 'never',
                label: t('never'),
                value: neverPresetValue
              }
            ]}
          />
        </div>
        <div className="button-row">
          <button
            className="primary-button"
            disabled={
              sending ||
              ((scope === 'SERVER' || targetNickname.trim().length > 0) && !formServerId) ||
              (targetNickname.trim().length > 0 && (scope !== 'SERVER' || targetNickname.trim().length < 3)) ||
              title.trim().length < 2 ||
              body.trim().length < 2
            }
          >
            {sending ? <ButtonLoading label={t('loading')} /> : editingNotification ? t('save') : t('sendNotification')}
          </button>
          {editingNotification ? (
            <button className="ghost-button" type="button" onClick={resetForm} disabled={sending}>
              {t('cancel')}
            </button>
          ) : null}
        </div>
      </form>
    </SectionCard>
      <SectionCard
        title={t('createdNotifications')}
        icon="bell"
        actions={
          <RefreshButton onClick={() => load(page, { showLoading: false })} label={t('refreshSection')} />
        }
      >
        {loading ? (
          <LoadingInline />
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="stack-md notifications-list">
            {items.map((item) => (
              <div key={item.id} className={cx('notification-card', editingNotification?.id === item.id && 'notification-card-active')}>
                <div className="notification-header-row">
                  <div>
                    <h3>{item.title}</h3>
                    <div className="meta-line">
                      #{item.id} · {item.serverName ?? t('global')} · {formatDate(locale, item.createdAt)}
                    </div>
                  </div>
                  <div className="notification-admin-actions">
                    <span className={badgeClass(item.severity)}>{item.severity}</span>
                    <button
                      className="ghost-button table-action-button"
                      type="button"
                      onClick={() => handleEdit(item)}
                      disabled={deletingNotificationId != null}
                      aria-label={t('edit')}
                      title={t('edit')}
                    >
                      <SvgIcon name="edit" className="button-svg-icon" />
                    </button>
                    <button
                      className="danger-button table-action-button"
                      type="button"
                      onClick={() => void handleDelete(item)}
                      disabled={deletingNotificationId != null}
                      aria-label={t('delete')}
                      title={t('delete')}
                    >
                      {deletingNotificationId === item.id ? (
                        <ButtonLoading label={t('loading')} />
                      ) : (
                        <SvgIcon name="trash" className="button-svg-icon" />
                      )}
                    </button>
                  </div>
                </div>
                <p className="notification-body">{item.body}</p>
                <NotificationAttachments
                  attachments={item.attachments ?? []}
                  locale={locale}
                  deletingAttachmentId={deletingAttachmentId}
                  onDelete={(attachment) => void handleDeleteAttachment(item, attachment)}
                />
                <div className="meta-line notification-admin-meta">
                  {t('scope')}: {item.scope === 'GLOBAL' ? t('global') : t('serverScope')} · {t('expiresAt')}: {formatDate(locale, item.expiresAt)}
                </div>
                <div className="meta-line notification-admin-meta">
                  {t('creator')}: {item.createdByName ?? 'System'} · {t('updatedAt')}: {formatDate(locale, item.updatedAt)}
                </div>
                {item.targetNickname || item.targetInviteCodeId ? (
                  <div className="meta-line notification-admin-meta">
                    {t('targetNickname')}: {item.targetNickname ?? '—'} · {t('targetInvite')}: {item.targetInviteCodeId ?? '—'}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
        {!loading ? <PaginationControls page={page} hasNext={hasNext} loading={loading} onPageChange={setPage} /> : null}
      </SectionCard>
    </div>
  );
}

function CommandQueueSection({ user, selectedServerId }: { user: SessionUser; selectedServerId: number | null }) {
  const { locale, t } = useI18n();
  const [items, setItems] = useState<CommandQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<ApiInfo | null>(null);
  const [command, setCommand] = useState('say Hello from Eternal Verifier');
  const immediatePresetValue = toDateTimeLocal(new Date(IMMEDIATE_UNIX_MILLIS));
  const [executeAfter, setExecuteAfter] = useState(immediatePresetValue);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const skipNextCommandPageLoadRef = useRef(false);

  const load = async (pageToLoad = page, options: LoadOptions = {}) => {
    const showLoading = options.showLoading ?? true;
    if (showLoading) {
      setLoading(true);
    }
    try {
      const query = new URLSearchParams();
      if (selectedServerId) query.set('serverId', String(selectedServerId));
      appendPaginationParams(query, pageToLoad);
      const payload = await api.get<CommandQueueItem[]>(`/api/admin/command-queue?${query.toString()}`);
      setItems(pageItems(payload));
      setHasNext(hasNextPage(payload));
      setInfo(keepSuccessInfo);
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    setPage(1);
    void load(1);
  }, [selectedServerId]);

  useEffect(() => {
    if (skipNextCommandPageLoadRef.current) {
      skipNextCommandPageLoadRef.current = false;
      return;
    }

    void load(page);
  }, [page]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (queueing) {
      return;
    }

    setQueueing(true);
    try {
      await api.post('/api/admin/command-queue', {
        serverId: selectedServerId,
        command,
        executeAfter: executeAfter || null
      });
      setExecuteAfter(immediatePresetValue);
      setInfo({ message: t('formCreated'), tone: 'success' });
      if (page !== 1) {
        skipNextCommandPageLoadRef.current = true;
        setPage(1);
      }
      await load(1, { showLoading: false });
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      setQueueing(false);
    }
  };

  return (
    <div className="page-grid two-columns">
      <SectionCard title={t('queueCommand')} icon="terminal">
        <Alert info={info} />
        <form className="stack-md" onSubmit={handleSubmit}>
          <label className="field-stack">
            <span>{t('command')}</span>
            <textarea className="textarea-input" value={command} onChange={(event) => setCommand(event.target.value)} />
          </label>
          <DateTimePresetField
            label={t('executeAfter')}
            value={executeAfter}
            onChange={setExecuteAfter}
            locale={locale}
            emptyLabel={t('notSet')}
            presets={[
              {
                id: 'now',
                label: t('now'),
                value: immediatePresetValue
              }
            ]}
          />
          <button className="primary-button" disabled={queueing || (!selectedServerId && user.isGlobalMaster) || command.trim().length < 1}>
            {queueing ? <ButtonLoading label={t('loading')} /> : t('queueCommand')}
          </button>
        </form>
      </SectionCard>

      <SectionCard
        title={t('commandQueue')}
        icon="gear"
        actions={
          <RefreshButton onClick={() => load(page, { showLoading: false })} label={t('refreshSection')} />
        }
      >
        {loading ? (
          <LoadingInline />
        ) : items.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="stack-md">
            {items.map((item) => (
              <div key={item.id} className="queue-card">
                <div className="queue-card-top">
                  <div>
                    <h3>#{item.id}</h3>
                    <div className="meta-line">{item.serverName ?? '—'} · {formatDate(locale, item.createdAt)}</div>
                  </div>
                  <span className={badgeClass(item.status)}>{humanStatus(t, item.status)}</span>
                </div>
                <code className="command-code">{item.commandText}</code>
                <div className="meta-line">
                  {t('executeAfter')}: {formatDate(locale, item.executeAfter)} · Attempts: {item.attemptCount}
                </div>
                {item.result ? <div className="result-box success-box">{item.result}</div> : null}
                {item.error ? <div className="result-box error-box">{item.error}</div> : null}
              </div>
            ))}
          </div>
        )}
        {!loading ? <PaginationControls page={page} hasNext={hasNext} loading={loading} onPageChange={setPage} /> : null}
      </SectionCard>
    </div>
  );
}

function AuditSection({ selectedServerId }: { selectedServerId: number | null }) {
  const { locale, t } = useI18n();
  const [items, setItems] = useState<AuditLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const skipNextAuditPageLoadRef = useRef(false);

  const load = async (pageToLoad = page, options: LoadOptions = {}) => {
    const showLoading = options.showLoading ?? true;
    if (showLoading) {
      setLoading(true);
    }
    try {
      const query = new URLSearchParams();
      if (selectedServerId) query.set('serverId', String(selectedServerId));
      if (search.trim()) query.set('search', search.trim());
      appendPaginationParams(query, pageToLoad);
      const payload = await api.get<AuditLogItem[]>(`/api/admin/audit-logs?${query.toString()}`);
      setItems(pageItems(payload));
      setHasNext(hasNextPage(payload));
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    setPage(1);
    void load(1);
  }, [selectedServerId]);

  useEffect(() => {
    if (skipNextAuditPageLoadRef.current) {
      skipNextAuditPageLoadRef.current = false;
      return;
    }

    void load(page);
  }, [page]);

  const handleSearch = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (searching) {
      return;
    }

    setSearching(true);
    try {
      if (page !== 1) {
        skipNextAuditPageLoadRef.current = true;
        setPage(1);
      }
      await load(1, { showLoading: false });
    } finally {
      setSearching(false);
    }
  };

  return (
    <SectionCard
      title={t('audit')}
      icon="scroll"
      className="audit-section-card"
      actions={
        <div className="toolbar-row compact-toolbar audit-toolbar">
          <form
            className="toolbar-row compact-toolbar search-toolbar"
            onSubmit={handleSearch}
          >
            <div className="search-input-wrap">
              <input
                className="text-input search-input"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('search')}
                autoComplete="off"
              />
              <button className="ghost-button search-input-button" type="submit" disabled={searching} aria-label={t('search')} title={t('search')}>
                {searching ? <ButtonLoading label={t('loading')} /> : <SvgIcon name="search" className="button-svg-icon" />}
              </button>
            </div>
          </form>
          <RefreshButton onClick={() => load(page, { showLoading: false })} label={t('refreshSection')} />
        </div>
      }
    >
      {loading ? (
        <LoadingInline />
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="stack-md">
          {items.map((item) => (
            <div key={item.id} className="audit-card">
              <div className="audit-top">
                <div>
                  <h3>{item.eventType}</h3>
                  <div className="meta-line">
                    {item.actorName ?? 'System'} · {item.serverName ?? '—'} · {formatDate(locale, item.createdAt)}
                  </div>
                </div>
                <span className="badge badge-muted">#{item.id}</span>
              </div>
              <p>{item.message}</p>
              <pre className="metadata-block">{JSON.stringify(item.metadata, null, 2)}</pre>
            </div>
          ))}
        </div>
      )}
      {!loading ? <PaginationControls page={page} hasNext={hasNext} loading={loading} onPageChange={setPage} /> : null}
    </SectionCard>
  );
}

function ErrorsSection() {
  const { locale, t } = useI18n();
  const [items, setItems] = useState<ErrorLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState<ApiInfo | null>(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);

  const load = async (pageToLoad = page, options: LoadOptions = {}) => {
    const showLoading = options.showLoading ?? true;
    if (showLoading) {
      setLoading(true);
    }
    try {
      const query = new URLSearchParams();
      appendPaginationParams(query, pageToLoad);
      const payload = await api.get<ErrorLogItem[]>(`/api/admin/error-logs?${query.toString()}`);
      setItems(pageItems(payload));
      setHasNext(hasNextPage(payload));
      setInfo(keepSuccessInfo);
    } catch (error) {
      setInfo({ message: (error as Error).message, tone: 'error' });
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    void load(page);
  }, [page]);

  return (
    <SectionCard
      title={t('errors')}
      icon="alertTriangle"
      actions={
        <RefreshButton onClick={() => load(page, { showLoading: false })} label={t('refreshSection')} />
      }
    >
      <Alert info={info} />
      {loading ? (
        <LoadingInline />
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>{t('errorId')}</th>
                <th>{t('diagnosticCode')}</th>
                <th>{t('description')}</th>
                <th>{t('createdAt')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>#{item.id}</td>
                  <td><code>{item.errorId}</code></td>
                  <td><code>{item.diagnosticCode ?? '—'}</code></td>
                  <td>
                    <div>{item.message}</div>
                    {item.debugMessage ? <div className="subtle-text">{item.debugMessage}</div> : null}
                    {item.requestMethod || item.requestPath ? (
                      <div className="meta-line">
                        {t('endpoint')}: {[item.requestMethod, item.requestPath].filter(Boolean).join(' ')}
                      </div>
                    ) : null}
                  </td>
                  <td>{formatDate(locale, item.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading ? <PaginationControls page={page} hasNext={hasNext} loading={loading} onPageChange={setPage} /> : null}
    </SectionCard>
  );
}

function DetailItem({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="detail-item">
      <div className="detail-label">{label}</div>
      <div className="detail-value">{value}</div>
    </div>
  );
}

function MetricCard({ title, value }: { title: string; value: number }) {
  return (
    <div className="metric-card">
      <div className="metric-title">{title}</div>
      <div className="metric-value">{value}</div>
    </div>
  );
}

export default AppShell;
